// Bildschirm "Kontakte" -- Liste und Suche auf EINER Seite (ST-SEA-001,
// ST-SEA-002; L13-01).
//
// Zwei Register: "Alle Kontakte" blaettert ohne Suchgrund die zuletzt
// angelegten Kontakte (ADR-0042) samt Cursor und Massenpflege und zeigt mit
// Suchgrund die Treffer; "Zuletzt von mir bearbeitet" zeigt die eigenen zehn.
// Das Register steht in der Adresse (`?ansicht=zuletzt`), der Suchbegriff nie.
// Das Feld "Liste filtern" ist das erste bedienbare Element im Register (kein
// Autofokus: der Rahmen setzt den Fokus auf `main`, damit "Kontakte" angesagt
// wird). Die fruehere Adresse `/kontakte/alle` leitet hierher um.
//
// WAS DIE SUCHE IST, und der Bildschirm sagt es selbst: ein Vergleich nach Name
// oder exakter Kennung -- tolerant gegen Gross-/Kleinschreibung, Umlaute,
// Bindestriche und Teilstrings, aber ohne Aehnlichkeit und ohne Rangfolge. Ein
// Suchfeld, das Aehnliches verspraeche, waere die Attrappe aus ADR-0002.
//
// GENAU EINE HAUPTAKTION JE ZUSTAND (CM-UX-002, NFR-UX-004): ohne Auswahl
// "Suchen"; sobald Kontakte ausgewaehlt sind, "Stichwort pflegen" (Suchen wird
// zurueckgestuft, nicht entfernt); im offenen Panel "Vorschau zeigen"
// (Massenpflege, ST-TAX-002 Batch-Schnitt, ADR-0058 Ziffer 13; Experience-
// Review 28.09.2026, P2). Einen Kontakt oeffnen bleibt
// ein Link auf jeder Zeile -- ein echtes `<a href>`, damit "in neuem Tab
// oeffnen" und "Adresse kopieren" funktionieren. "Weitere Kontakte laden" ist
// ein zurueckhaltender Knopf.
//
// DER BEGRIFF STEHT NIE IN DER ADRESSE UND IN KEINEM SPEICHER. Er ist ein
// Personendatum; er geht im Rumpf eines POST zum Server (ADR-0044 Ziffer 3)
// und liegt fuer den Zurueck-Weg im fluechtigen Anwendungszustand, der mit
// der Abmeldung endet (parties.ts, ADR-0049).

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Dropdown,
  Field,
  Input,
  Option,
  Radio,
  RadioGroup,
  makeStyles,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Subtitle1,
  Switch,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { identifierTypeLabel } from '../identifiers.ts';
import {
  fetchPartyList,
  kindEmptyHeading,
  kindViewNotice,
  lastChangedLabelOf,
  organizationLineOf,
  type PartyKindFilter,
  fetchRecentlyEditedParties,
  kindLabel,
  listSummary,
  resultCountLabel,
  matchedIdentifierNotice,
  matchedLegalNameNotice,
  matchedViaOrganizationNotice,
  activeFilterCount,
  addressFilterProblems,
  EMPTY_SEARCH_FILTER,
  filterNarrowsSearch,
  matchSummary,
  partyHref,
  recallSearchFilter,
  recallSearchTerm,
  rememberSearchFilter,
  rememberSearchTerm,
  searchParties,
  fetchSearchOptions,
  MAXIMUM_FILTER_TERMS,
  searchTermProblem,
  type AddressFilterProblems,
  type PartySummary,
  type SearchFilter,
} from '../parties.ts';
import { Verbundene } from './kontakte-verbundene.tsx';
import { EXPORT_BUTTON_ID, ExportKnopf, ExportMeldung, type ExportMeldungArt, type ExportOmitChannel, type ExportQuelle } from './kontakte-export.tsx';
import { ZurSammlungKnopf } from './zur-sammlung.tsx';
import { checkForS1, s1FormToText, usesQuerySyntax } from '../suche/abfrage/s1-form.ts';
import { applyAction, hintMessage, type MessagePart } from '../suche/abfrage/saetze.ts';
import { MAXIMUM_QUERY_LENGTH } from '../suche/abfrage/syntax.ts';
import type { Span } from '../suche/abfrage/baum.ts';
import { uebernimmHausArten } from '../suche/abfrage/felder.ts';
import { fetchTermSuggestions, resolveS1Form, type ResolvedS1, type TermSuggestion } from '../suche/aufloesung.ts';
import { ersetzen, vorschlagLage, vorschlagUebernehmen } from '../suche/vorschlaege.ts';
import { feldlisteLaden } from '../suche/feldliste.ts';
import { useCombobox } from '../shell/use-combobox.ts';
import { Tasten } from '../shell/taste.tsx';
import { useEntprellterAbruf } from '../shell/use-entprellter-abruf.ts';
import { Suchhilfe } from '../suche/suchhilfe.tsx';
import { Symbol } from '../shell/symbole.tsx';
import { meldungsIntent } from '../shell/use-section-commands.ts';
import { weitereOrganisationen } from '../via-organization.ts';
import { roleLabel } from '../context-roles.ts';
import {
  AktiveFilter,
  FILTER_LOCALITY_ID,
  FILTER_POSTAL_CODE_ID,
  FilterBereich,
  FilterKnopf,
  useFilterOptionen,
} from './kontakte-filter.tsx';
import { fetchAssignableTerms, truncationNotice, type AssignableTermView } from '../classifications.ts';
import { mitAnmeldung } from '../shell/anmeldung.ts';
import { alsEintrag, searchAssignableEntries } from '../stichwort-quelle.ts';
import { brauchtSuchfeld, StichwortSuchfeld, type StichwortEintrag, type StichwortTreffer } from '../suche/stichwort-suchfeld.tsx';
import {
  createBatch,
  massenlaufHref,
  recallSelection,
  rememberBatchRequest,
  rememberSelection,
  exceedsBatch,
  maintenanceLimitSentence,
  selectionDiscardedNotice,
  selectionHeldNotice,
  selectionSummary,
  togglePage,
  toggleSelected,
  PREVIEW_UNCERTAIN_MESSAGE,
  UNREACHABLE_MESSAGE,
  type BatchOperation,
  type SelectedParty,
} from '../classification-batches.ts';
import { AppLink, navigate, setzeAdressParameter, useAdressParameter } from '../shell/router.tsx';
import { VorgangNach, Zustand, type ZustandsAktion } from '../shell/zustand.tsx';
import { useScreenStyles } from '../shell/screen-styles.ts';
import { StatusPille } from '../shell/status-pille.tsx';
import { Schaltergruppe } from '../shell/schaltergruppe.tsx';
import { Tabelle, type TabellenSpalte } from '../shell/tabelle.tsx';
import { TypSymbol } from '../shell/typ-symbol.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

/** Lesende Abrufe erneuern eine abgelaufene Anmeldung still und wiederholen einmal. */
const LESEN = mitAnmeldung();

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    // Die Zeile bricht nach der Breite der Karte um, nicht nach dem Fenster (DESIGN P3 zu #563).
    containerType: 'inline-size',
    containerName: 'kontaktwerkzeug',
  },
  // Der Satz unter dem Feld und am Zeilenende die Suchhilfe (DESIGN P3 zu #524).
  hinweiszeile: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: '0.75rem' },
  field: {
    // Ausgang schmal, dann wachsen: sonst naehme der lange Hinweis die
    // Hoechstbreite als Ausgang, und die Zeile braeche schon bei 1024 um.
    flexGrow: 1,
    flexBasis: '14rem',
    minWidth: '14rem',
    // Das Feld fuellt den Rest der Zeile („Feld (flex)“, DESIGN zu L13-01), damit
    // Hinweis, Suchhilfe und „Filter“ in einer Zeile stehen; schmal volle Breite.
    // Schmal teilt sich das Feld die Zeile mit „?“ und „Filter“ (DESIGN L14-04);
    // bei grossem Text (200 %) brechen die Knoepfe unter das Feld, statt es auf
    // wenige Zeichen zu druecken.
    '@container kontaktwerkzeug (max-width: 40rem)': { flexBasis: '9rem', minWidth: 0 },
  },
  // Rechts am Feld, auf Hoehe des Eingabefelds (DESIGN L14-03, v4).
  feldKnoepfe: {
    display: 'flex',
    gap: '0.5rem',
    marginBlockStart: `calc(${tokens.lineHeightBase300} + ${tokens.spacingVerticalXXS})`,
  },
  nurBreit: { '@container kontaktwerkzeug (max-width: 40rem)': { display: 'none' } },
  nurSchmal: { display: 'none', '@container kontaktwerkzeug (max-width: 40rem)': { display: 'inline-flex' } },
  // Schmal ein Segment ueber die volle Breite: die Teile wachsen nach ihrem
  // Inhalt („Organisationen“ ist breiter als ein Drittel bei 390 px); reicht
  // die Breite nicht (320), bricht es um statt quer zu scrollen.
  segment: {
    '@container kontaktwerkzeug (max-width: 40rem)': {
      width: '100%',
      columnGap: '0.25rem',
      '& > button': { flexGrow: 1, paddingInline: '0.5rem' },
    },
  },
  // Schmal nur das Symbol; der Name bleibt ueber aria-label (DESIGN L14-04).
  filterKnopf: { '@container kontaktwerkzeug (max-width: 40rem)': { minWidth: 'auto', paddingInline: '0.4375rem' } },
  // Der Umschalter steht auf Hoehe des Eingabefelds, nicht mittig zu Beschriftung,
  // Feld und Hinweis: um die Beschriftung nach unten (DESIGN P2 zu #563).
  umschalterPlatz: {
    marginBlockStart: `calc(${tokens.lineHeightBase300} + ${tokens.spacingVerticalXXS})`,
    '@container kontaktwerkzeug (max-width: 40rem)': { marginBlockStart: 0, flexBasis: '100%' },
  },
  lupe: { minWidth: '24px', minHeight: '24px' },
  // Die Vorschlagsliste liegt UEBER dem Inhalt darunter: sie verschiebt beim
  // Tippen weder Chips noch Tabelle (DESIGN P2-5). Hoechstens min(50vh, 20rem).
  // minmax(0, 1fr): ein <input> traegt eine Eigenbreite und schoebe sonst bei 320 px ueber die Knoepfe daneben.
  eingabeAnker: { position: 'relative', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', minWidth: 0 },
  vorschlagListe: {
    position: 'absolute',
    zIndex: 10,
    insetInline: 0,
    top: 'calc(100% + 4px)',
    maxHeight: 'min(50vh, 20rem)',
    overflowY: 'auto',
    padding: '0.25rem',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
  },
  vorschlag: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: '0.75rem',
    padding: '0.5rem 0.75rem',
    minHeight: '2.75rem',
    boxSizing: 'border-box',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    overflowWrap: 'anywhere',
    color: tokens.colorNeutralForeground1,
    // Die aktive Option mit Flaeche, Gewicht UND Balken, nie nur Farbe (wie die Palette).
    '&[aria-selected="true"]': {
      backgroundColor: tokens.colorBrandBackground2,
      color: tokens.colorBrandForeground2,
      fontWeight: tokens.fontWeightSemibold,
      boxShadow: `inset 3px 0 0 ${tokens.colorBrandStroke1}`,
    },
  },
  // Vorgemerkt fuer Tab: nur eine leichte Flaeche, die Pfeilwahl bleibt deutlicher.
  vorgemerkt: { backgroundColor: tokens.colorNeutralBackground1Hover },
  vorschlagRechts: { display: 'inline-flex', alignItems: 'baseline', gap: '0.5rem', flexShrink: 0 },
  // Nur fuer den Namen: inline und ohne Position, sonst setzt der Browser
  // beim Berechnen des Namens Leerzeichen um das Komma.
  kommaLeser: { fontSize: 0 },
  vorschlagAchse: { color: tokens.colorNeutralForeground2, fontWeight: tokens.fontWeightRegular },
  // Eingabeteile im Hinweis in Festbreitenschrift, ohne Rahmen (DESIGN P3-2).
  eingabeTeil: { fontFamily: tokens.fontFamilyMonospace },
  hinweisKnoepfe: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginInlineStart: '0.5rem',
    // Bei 390 in eigener Zeile unter dem Satz, linksbuendig (DESIGN).
    '@media (max-width: 40em)': { display: 'flex', marginInlineStart: 0, marginBlockStart: '0.25rem' },
  },
  // Mindestens 28 px wie v4 .btn.small (DESIGN P3 zu #510).
  hinweisKnopf: { minHeight: '28px' },
  // Die Registerleiste wie im Kontaktdetail (kontakt/stile.ts): das gewaehlte
  // Register mit Unterstrich UND staerkerer Schrift, nicht nur Farbe (WCAG 1.4.1).
  // Bei grossem Text (320 px, 200 %) brechen die Reiter um, statt hinter die
  // Fensterkante zu laufen (WCAG 1.4.10, DESIGN P2 zu #599); die Tastatur nach APG bleibt.
  register: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.25rem',
    maxWidth: '100%',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  registerKnopf: {
    appearance: 'none',
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    marginBottom: '-1px',
    padding: '0.5rem 0.75rem',
    minHeight: '2.5rem',
    cursor: 'pointer',
    maxWidth: '100%',
    textAlign: 'start',
    overflowWrap: 'anywhere',
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    ':hover': { color: tokens.colorNeutralForeground1, backgroundColor: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '-2px' },
  },
  registerGewaehlt: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    borderBottomColor: tokens.colorBrandStroke1,
  },
  registerInhalt: { marginTop: '0.75rem' },
  // Der aufgeklappte Filterbereich steht in voller Breite zwischen Feld und "Suchen".
  filterZeile: {
    flexBasis: '100%',
  },

  hint: {
    display: 'block',
    marginTop: '0.25rem',
    // Umbruch statt Querscrollen, dasselbe Muster wie kontaktwege.tsx und
    // screen-styles.ts: eine E-Mail-Adresse ist ein langes Wort ohne
    // Trennstelle und sprengte sonst den 320-Pixel-Reflow (Experience-Review
    // Teil B, 29.09.2026; Probe C9 mit langer Adresse).
    overflowWrap: 'anywhere',
  },
  // Die Ergebniszeile ueber der Tabelle (v4): Zahl, Auswahlsatz und Schalter in einer Reihe.
  ergebniszeile: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.25rem 0.5rem',
    marginTop: '0.75rem',
    color: tokens.colorNeutralForeground2,
  },
  statusZeile: { minWidth: 0, overflowWrap: 'anywhere' },
  ohneUmbruch: { whiteSpace: 'nowrap' },
  // Der Strich einer Zelle ohne Wert tritt hinter die Daten zurueck (DESIGN zu C5 P3-1);
  // in erzwungenen Farben bleibt er Text.
  ohneWert: { color: tokens.colorNeutralForeground3, '@media (forced-colors: active)': { color: 'CanvasText' } },
  nurLeser: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
  // Umschalter | Feld in EINER Zeile von links (v4 v01); schmal steht der
  // Umschalter ueber dem Feld. Kein Aussenabstand oben: die Karte traegt ihn.
  werkzeugzeile: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: '0.5rem 1rem',
  },
  auswahlSatz: { color: tokens.colorNeutralForeground2 },
  // Die Region steht vorn, ist aber nur fuer Screenreader; die Kurzform folgt ihr sichtbar.
  kurzform: { order: -1 },
  // Name in der Tabelle: Typ-Symbol und fetter Name, immer unterstrichen.
  // Abweichung von v4 (dort erst bei Zeiger): auf Touch gibt es keinen Zeiger,
  // und ein Link darf nicht nur an Farbe oder Lage erkennbar sein (WCAG 1.4.1,
  // ADR-0002).
  tabellenName: {
    display: 'inline-flex',
    alignItems: 'center',
    // Das einzige Ziel, das den Kontakt oeffnet: 44 px hoch wie die Zeile
    // (Fingerziel).
    minHeight: '2.75rem',
    gap: '0.5rem',
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    textDecorationLine: 'underline',
    textUnderlineOffset: '2px',
    // Bricht der Name um, bleibt das Typ-Symbol gleich gross.
    '& > svg': { flexShrink: 0 },
    // Ein langes Namensglied ("Beispielhausen-Probenachname") ist sonst bei
    // 320 px und 200 % die Mindestbreite der Tabelle und schiebt sie ueber den
    // Kartenrand (Restliste B8). Silbentrennung nach lang="de" statt
    // overflow-wrap anywhere, das auch kurze Namen mitten im Wort bricht.
    hyphens: 'auto',
  },
  tabellenHinweis: {
    display: 'block',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    overflowWrap: 'anywhere',
  },
  // "Anschrift" und das Kettenzeichen brechen nicht getrennt um; Abstand und Hoehe wie am Kontakt.
  geerbtZeichen: {
    whiteSpace: 'nowrap',
    '& > svg': { marginInlineStart: tokens.spacingHorizontalXS, verticalAlign: '-0.2em' },
  },
  // Exportieren und Zur Sammlung in einer Zeile; die Statusregionen darunter.
  weiterwege: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.25rem',
    '& > [data-zur-sammlung-ergebnis]': { flexBasis: '100%' },
  },
  panel: {
    marginTop: '1rem',
    paddingTop: '0.75rem',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
});

const zeilenSchluessel = (party: PartySummary): string => party.partyId;

const AUSWAHL_QUELLE: ExportQuelle = Object.freeze({ art: 'auswahl' });

/** Die drei Ansichten des Umschalters; "alle" heisst ohne Einengung. */
/** Vorgelesen in einer Zelle ohne Wert (Restliste C5 P3-1). */
const OHNE_ANGABE = 'keine Angabe';

/** Die Tabelle heisst wie die Art, auf die der Umschalter sie einengt (Restliste C5 P3-3). */
function tabellenNameNachArt(art: PartyKindFilter | undefined): string {
  return art === 'person' ? 'Personen' : art === 'organization' ? 'Organisationen' : 'Kontakte';
}

const ART_OPTIONEN = Object.freeze([
  Object.freeze({ id: 'person', label: 'Personen' }),
  Object.freeze({ id: 'organization', label: 'Organisationen' }),
  Object.freeze({ id: 'alle', label: 'Alle' }),
]);


export interface KontakteScreenProps {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

/** `suche` ist `/kontakte`, `alle` ist `/kontakte/alle`. */
type Modus = 'suche' | 'alle';

/** `ruht`: die Einstiegsseite ohne Begriff -- es wurde nichts abgerufen. */
type Status = 'ruht' | 'laedt' | 'geladen' | 'abgelehnt';

const PFLEGE_GRENZE_ID = 'kontakte-pflege-grenze';
const PFLEGE_GRENZE_FELD_ID = 'kontakte-pflege-grenze-feld';

/** Die DOM-Kennungen. Dieselbe Bauart wie FIELD_INPUT_IDS. */
const MORE_BUTTON_ID = 'kontakte-weitere';
const SEARCH_INPUT_ID = 'kontakte-suchbegriff';
const VORSCHLAG_LISTE_ID = 'kontakte-vorschlaege';
/** Wie in der Palette: so lange wartet der Abruf nach dem letzten Tastendruck. */
const VORSCHLAG_VERZOEGERUNG_MS = 250;
const KEINE_BEGRIFFE: readonly TermSuggestion[] = [];

/** Eine Option der Vorschlagsliste, gleich ob Begriff aus S0, Feld, Wert oder ODER. */
interface Vorschlag {
  readonly id: string;
  readonly titel: string;
  readonly zusatz?: string;
  readonly uebernehmen: () => { readonly text: string; readonly cursor: number };
}

/**
 * Die DOM-Kennung der Statusregion.
 *
 * Sie ist das Ziel des Fokus nach einer Suche und das AUSWEICHZIEL nach der
 * letzten Seite: der Nachladeknopf verschwindet mit ihr, und ein Fokus, der
 * auf ein entferntes Element zeigte, faellt auf das Dokument zurueck
 * (WCAG 2.4.3). Die Statusregion traegt in beiden Faellen die Auskunft, die
 * der Druck ausgeloest hat.
 */
const STATUS_REGION_ID = 'kontakte-status';

/** Die Statusregion des Abschnitts "Stichwort pflegen" -- eigene Region je Abschnitt, mit unterscheidendem Namen. */
const PANEL_STATUS_REGION_ID = 'kontakte-massenpflege-status';
const PANEL_TITLE_ID = 'kontakte-massenpflege-titel';
const PANEL_TERM_INPUT_ID = 'kontakte-massenpflege-stichwort';

/** Die Spalten der Kontaktliste, gemeinsam fuer beide Register. Stabil ueber Darstellungen: die Zeilen zeichnen nur neu, wenn sich die Spalten aendern. */
function useKontaktSpalten(
  /** Die Organisation des Filters „Ansprechpartner:innen von“: ihre Herkunft steht an keiner Zeile ein zweites Mal (DESIGN zu E3 S2b). */
  filterOrganisation?: { readonly partyId: string; readonly displayName: string },
): readonly TabellenSpalte<PartySummary>[] {
  const { tabellenName, tabellenHinweis, nurLeser, ohneUmbruch, ohneWert, geerbtZeichen } = useStyles();
  // Eine leere Zelle saehe nach nicht geladenen Daten aus (Restliste C5 P3-1): sichtbar ein
  // Strich, vorgelesen ein Wort statt des Zeichens. Unter dem Namen gefaltet bleibt eine
  // fehlende Angabe weg, dort steht keine Spalte, die leer aussehen koennte.
  return useMemo((): readonly TabellenSpalte<PartySummary>[] => {
    const strich = (vorgelesen: string) => (
      <>
        <span aria-hidden="true" className={ohneWert}>—</span>
        <span className={nurLeser}>{vorgelesen}</span>
      </>
    );
    return [
      {
        id: 'name',
        kopf: 'Name',
        zelle: (party) => (
          <>
            <AppLink to={partyHref(party.partyId)} className={tabellenName}>
              <TypSymbol organisation={party.kind === 'organization'} groesse={20} />
              {party.displayName}
            </AppLink>
            {/* Die Art traegt das Symbol; fuer Hilfsmittel steht sie als Text daneben,
                ausserhalb des Links, dessen Name der Kontakt bleibt. */}
            <span className={nurLeser}>, {kindLabel(party.kind)}</span>
            {party.matchedIdentifier !== undefined ? (
              // Woran der Treffer liegt, wenn nicht am Namen (CM-SEA-001).
              <Caption1 className={tabellenHinweis}>
                {matchedIdentifierNotice(party.matchedIdentifier, identifierTypeLabel)}
              </Caption1>
            ) : undefined}
            {party.matchedLegalName !== undefined ? (
              <Caption1 className={tabellenHinweis}>{matchedLegalNameNotice(party.matchedLegalName)}</Caption1>
            ) : undefined}
            {/* Treffer ueber den Namen der Organisation (CM-SEA-001): sonst stuende
                eine Person ohne erkennbaren Grund in der Liste. */}
            {party.matchedViaOrganization !== undefined &&
            party.matchedViaOrganization !== party.matchedViaOrganizationAddress?.displayName &&
            party.matchedViaOrganization !== filterOrganisation?.displayName ? (
              <Caption1 className={tabellenHinweis}>{matchedViaOrganizationNotice(party.matchedViaOrganization)}</Caption1>
            ) : undefined}
            {/* Treffer ueber Ort/PLZ der Organisation (E3 S1b): dasselbe Zeichen und Wort wie die geerbte
                Anschrift am Kontakt, die Organisation aber als Text -- eine Zeile, ein Link. Nennt der
                Namenstreffer dieselbe Organisation, steht nur diese Zeile. */}
            {party.matchedViaOrganizationAddress !== undefined &&
            party.matchedViaOrganizationAddress.organizationPartyId !== filterOrganisation?.partyId ? (
              <Caption1 className={tabellenHinweis}>
                <span className={geerbtZeichen}>
                  Anschrift
                  <span aria-hidden="true">{'\u2060'}</span>
                  <Symbol name="verknuepft" groesse="calc(1em + 0.25rem)" />
                </span>
                <span className={nurLeser}>, geerbt von </span>
                <span aria-hidden="true">{' ·\u00a0'}</span>
                {party.matchedViaOrganizationAddress.displayName}
                {party.matchedViaOrganizationAddress.moreCount > 0
                  ? ` ${weitereOrganisationen(party.matchedViaOrganizationAddress.moreCount) ?? ''}`
                  : ''}
              </Caption1>
            ) : undefined}
          </>
        ),
      },
      // Die sichtbare Organisation einer Person als Klartext und nicht als
      // zweiter Link: eine Zeile, ein Link (5.2 Nr. 2). Dieselbe Sichtbarkeit wie
      // im Kontaktdetail (Server).
      // Eine Organisation selbst hat keine Organisation: ein Strich statt einer
      // leeren Zelle, die nach fehlenden Daten aussaehe (M-09b); vorgelesen wird
      // "entfällt" statt des Zeichens.
      {
        id: 'organisation',
        kopf: 'Organisation',
        zelle: (party) => (party.kind === 'organization' ? strich('entfällt') : (organizationLineOf(party) ?? strich(OHNE_ANGABE))),
        stufe: 'schmal',
        kurz: organizationLineOf,
      },
      // Kontaktwege als Klartext und nicht als mailto/tel: eine Zeile, ein Link
      // (ST-SEA-001 Anzeige Teil B, ADR-0002).
      // Die E-Mail-Adresse ist ein Wort ohne Trennstelle: sie allein darf hart umbrechen.
      { id: 'email', kopf: 'E-Mail', zelle: (party) => party.primaryEmailAddress ?? strich(OHNE_ANGABE), stufe: 'schmal', kurz: (party) => party.primaryEmailAddress, umbruch: 'hart' },
      // Eine Rufnummer bricht nicht zwischen ihren Gruppen um.
      { id: 'telefon', kopf: 'Telefon', zelle: (party) => (party.primaryPhoneNumber === undefined ? strich(OHNE_ANGABE) : <span className={ohneUmbruch}>{party.primaryPhoneNumber}</span>), stufe: 'mittel', kurz: (party) => party.primaryPhoneNumber },
      // Schmal unter dem Namen statt als eigene Spalte: bei 320 px schnitt sie sonst ab.
      { id: 'status', kopf: 'Status', zelle: (party) => <StatusPille status={party.lifecycleStatus} />, stufe: 'schmal', gefaltet: (party) => <StatusPille status={party.lifecycleStatus} /> },
      // Die letzte Aenderung irgendwo am Kontakt; ohne Sortierung, die Liste
      // bleibt nach der Anlage geordnet.
      {
        id: 'geaendert',
        kopf: 'Geändert',
        zelle: (party) => lastChangedLabelOf(party) ?? strich(OHNE_ANGABE),
        stufe: 'mittel',
        kurz: (party) => {
          const datum = lastChangedLabelOf(party);
          return datum === undefined ? undefined : `geändert ${datum}`;
        },
      },
    ];
  }, [tabellenName, tabellenHinweis, nurLeser, ohneUmbruch, ohneWert, geerbtZeichen, filterOrganisation?.partyId, filterOrganisation?.displayName]);
}

/** Das Register der Seite steht in der Adresse, damit Zurueck und Lesezeichen es halten. */
const ANSICHT_PARAMETER = 'ansicht';
const REGISTER_ALLE = 'register-alle';
const REGISTER_ZULETZT = 'register-zuletzt';

/**
 * "Kontakte" als EINE Seite (L13-01): Register "Alle Kontakte" (Liste, Suche,
 * Auswahl) und "Zuletzt von mir bearbeitet". Fluents TabList bringt das
 * APG-Muster mit: Pfeile wechseln und aktivieren, der Fokus bleibt am Register.
 */
const ZULETZT_SPALTEN: ReadonlySet<string> = new Set(['name', 'email', 'status']);

const REGISTER = [
  { id: 'alle', label: 'Alle Kontakte', domId: REGISTER_ALLE },
  { id: 'zuletzt', label: 'Zuletzt von mir bearbeitet', domId: REGISTER_ZULETZT },
] as const;

/**
 * Der sichtbare Text des Aufklappers; der volle Name beginnt mit genau ihm (WCAG 2.5.3).
 * "Personen" statt "Ansprechpartner:innen": das lange Wort brach bei 320 px und 200 %
 * mitten durch, und es bildet das Paar zu "Organisationen" (DESIGN ansprech-knopf).
 * Das Fachwort bleibt, wo die Beziehung gemeint ist: Filterchip und Statuszeilen.
 */
function aufklappWort(party: Pick<PartySummary, 'kind'>): string {
  return party.kind === 'organization' ? 'Personen' : 'Organisationen';
}

export function KontakteScreen(props: KontakteScreenProps): ReactElement {
  const own = useStyles();
  const ansicht = useAdressParameter(ANSICHT_PARAMETER) === 'zuletzt' ? 'zuletzt' : 'alle';
  const waehlen = (id: 'alle' | 'zuletzt'): void => setzeAdressParameter(ANSICHT_PARAMETER, id === 'zuletzt' ? 'zuletzt' : undefined);
  const beiTaste = (ereignis: KeyboardEvent<HTMLButtonElement>): void => {
    // Mit Modifikator gehoeren die Tasten dem Browser (Alt+Pfeil: zurueck).
    if (ereignis.altKey || ereignis.ctrlKey || ereignis.metaKey || ereignis.shiftKey) return;
    const stelle = REGISTER.findIndex((eintrag) => eintrag.id === ansicht);
    const ziel =
      ereignis.key === 'ArrowRight'
        ? (stelle + 1) % REGISTER.length
        : ereignis.key === 'ArrowLeft'
          ? (stelle - 1 + REGISTER.length) % REGISTER.length
          : ereignis.key === 'Home'
            ? 0
            : ereignis.key === 'End'
              ? REGISTER.length - 1
              : undefined;
    const neu = ziel === undefined ? undefined : REGISTER[ziel];
    if (neu === undefined) return;
    ereignis.preventDefault();
    waehlen(neu.id);
    document.getElementById(neu.domId)?.focus();
  };
  return (
    <>
      {/* Eigene Leiste statt Fluent TabList, wie im Kontaktdetail: deren
          Tastaturfuehrung (tabster) legt unsichtbare, fokussierbare
          aria-hidden-Waechter an, die axe meldet. Verhalten nach dem
          ARIA-Muster: ein Tabstopp, Pfeile, Pos1 und Ende wechseln und waehlen
          zugleich, der Fokus bleibt am Register. */}
      <div role="tablist" aria-label="Ansicht der Kontakte" className={own.register}>
        {REGISTER.map((eintrag) => {
          const gewaehlt = eintrag.id === ansicht;
          return (
            <button
              key={eintrag.id}
              type="button"
              role="tab"
              id={eintrag.domId}
              aria-selected={gewaehlt}
              aria-controls="kontakte-register-inhalt"
              tabIndex={gewaehlt ? 0 : -1}
              className={mergeClasses(own.registerKnopf, gewaehlt && own.registerGewaehlt)}
              onClick={() => waehlen(eintrag.id)}
              onKeyDown={beiTaste}
            >
              {eintrag.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id="kontakte-register-inhalt" aria-labelledby={ansicht === 'zuletzt' ? REGISTER_ZULETZT : REGISTER_ALLE} className={own.registerInhalt}>
        {ansicht === 'zuletzt' ? <ZuletztAnsicht {...props} /> : <KontakteAnsicht {...props} />}
      </div>
    </>
  );
}

/**
 * "Zuletzt von mir bearbeitet" (F1/F2): Kontakte, an denen die letzte
 * Aenderung von mir stammt, hoechstens zehn. Dieselbe Tabelle ohne Auswahl:
 * gepflegt wird aus "Alle Kontakte" (DESIGN F2).
 */
function ZuletztAnsicht({ apiBaseUrl, accessToken, onReauthenticate }: KontakteScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  // Die Quelle liefert weder Organisation noch Telefon noch letzte Aenderung:
  // leere Spalten fallen weg, statt leer zu stehen (DESIGN P3 zu #554).
  const alleSpalten = useKontaktSpalten();
  const spalten = useMemo(() => alleSpalten.filter((spalte) => ZULETZT_SPALTEN.has(spalte.id)), [alleSpalten]);
  const [stand, setStand] = useState<readonly PartySummary[] | 'laedt' | 'gestoert'>('laedt');
  const [versuch, setVersuch] = useState(0);
  useEffect(() => {
    let aktuell = true;
    void (async () => {
      try {
        const token = await accessToken();
        if (!aktuell) return;
        if (token === undefined) {
          onReauthenticate();
          return;
        }
        const result = await fetchRecentlyEditedParties({ baseUrl: apiBaseUrl, accessToken: token });
        if (aktuell) setStand(result.outcome === 'loaded' ? result.parties : 'gestoert');
      } catch {
        if (aktuell) setStand('gestoert');
      }
    })();
    return () => {
      aktuell = false;
    };
  }, [accessToken, apiBaseUrl, onReauthenticate, versuch]);

  // Nach "Erneut versuchen" haengt der Knopf aus: der Fokus geht auf die Region mit dem Ergebnis.
  const region = useRef<HTMLDivElement | null>(null);
  const fokusNachLaden = useRef(false);
  useEffect(() => {
    if (stand !== 'laedt' && fokusNachLaden.current) {
      fokusNachLaden.current = false;
      region.current?.focus();
    }
  }, [stand]);

  return (
    <Card className={styles.card}>
      {/* EINE Region traegt Laden, Ergebnis, Leerlage und Stoerung: sie ist von
          Anfang an eingehaengt, damit jeder Wechsel angesagt wird. */}
      <div ref={region} aria-live="polite" role="status" aria-label="Status: Zuletzt bearbeitet" tabIndex={-1} className={own.statusZeile}>
        {stand === 'laedt' ? <Spinner labelPosition="after" label="Kontakte werden geladen" /> : undefined}
        {stand === 'gestoert' ? (
          <Zustand
            art="fehler"
            titel="Deine zuletzt bearbeiteten Kontakte lassen sich gerade nicht laden."
            saetze={['Versuch es gleich noch einmal.']}
            haupt={{
              label: 'Erneut versuchen',
              onClick: () => {
                fokusNachLaden.current = true;
                region.current?.focus();
                setStand('laedt');
                setVersuch((zahl) => zahl + 1);
              },
            }}
          />
        ) : undefined}
        {Array.isArray(stand) && stand.length === 0 ? <Zustand art="leer" eingebettet titel="Du hast noch keinen Kontakt bearbeitet." saetze={[]} /> : undefined}
        {Array.isArray(stand) && stand.length > 0 ? (
          <Caption1>{stand.length === 1 ? 'Der Kontakt, den du zuletzt bearbeitet hast.' : `Die ${stand.length} Kontakte, die du zuletzt bearbeitet hast.`}</Caption1>
        ) : undefined}
      </div>
      {Array.isArray(stand) && stand.length > 0 ? <Tabelle name="Zuletzt von mir bearbeitet" zeilen={stand} schluessel={zeilenSchluessel} spalten={spalten} /> : undefined}
    </Card>
  );
}

function KontakteAnsicht({ apiBaseUrl, accessToken, onReauthenticate }: KontakteScreenProps): ReactElement {
  const styles = useScreenStyles();
  const own = useStyles();
  const [parties, setParties] = useState<readonly PartySummary[]>([]);
  /** Der Zurueck-Weg aus dem Kontakt findet die Suche wieder: sie bleibt im Anwendungszustand. */
  const [gemerkterBegriff] = useState<string | undefined>(recallSearchTerm);
  const [gemerkterFilter] = useState<SearchFilter | undefined>(recallSearchFilter);
  // Ohne Suche blaettert die Seite (L13-01): es wird immer etwas abgerufen.
  const [status, setStatus] = useState<Status>('laedt');
  const [message, setMessage] = useState<PlainMessage | undefined>(undefined);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  /**
   * Der Cursor, dessen Abruf gescheitert ist.
   *
   * Ohne ihn holte "Erneut versuchen" die ERSTE Seite, und ein geglueckter
   * zweiter Versuch ersetzte alles bereits Geladene durch Seite eins -- die
   * Nutzerin verlaere genau die Stelle, an der sie war. `undefined` heisst
   * "die erste Seite ist gescheitert"; dann ist Seite eins auch die
   * Wiederholung.
   */
  const [fehlgeschlagenerCursor, setFehlgeschlagenerCursor] = useState<string | undefined>(undefined);
  /**
   * Der Begriff, mit dem die angezeigte Liste entstanden ist -- `undefined`
   * heisst auf `/kontakte/alle` "die Liste der zuletzt angelegten Kontakte"
   * und auf `/kontakte` "noch nichts gesucht". Er ist vom Text im Feld
   * GETRENNT: was jemand tippt, aendert die Liste erst mit "Suchen". Beim
   * Mount kommt er aus dem fluechtigen Anwendungszustand, damit der
   * Zurueck-Weg aus dem Kontaktdetail die Trefferliste wiederfindet.
   */
  const [aktiverBegriff, setAktiverBegriff] = useState<string | undefined>(gemerkterBegriff);
  /**
   * Die angewandten Filter -- `undefined` heisst "keine Suche" (Liste oder
   * Ruhezustand). Seit den Facetten wird auf `/kontakte` jede Suche MIT Filter
   * geschickt, mindestens mit "ohne Inaktive" (E3). Getrennt vom Entwurf im
   * Bereich: ein Kaestchen aendert die Liste erst mit "Suchen".
   */
  const [aktiverFilter, setAktiverFilter] = useState<SearchFilter | undefined>(gemerkterFilter);
  const [entwurf, setEntwurf] = useState<SearchFilter>(gemerkterFilter ?? EMPTY_SEARCH_FILTER);
  const [filterOffen, setFilterOffen] = useState(false);
  // Der Export (ST-SEA-002 F2): Dialog und Abschnitt "Meine Exporte".
  const [exportDialogOffen, setExportDialogOffen] = useState(false);
  // Nach dem Anlegen eine Meldung mit dem Weg zur Liste (L13-01a); die Liste
  // selbst steht auf /exporte.
  const [exportMeldung, setExportMeldung] = useState<{ readonly art: ExportMeldungArt; readonly weggelassen?: ExportOmitChannel | undefined; readonly nr: number } | undefined>(undefined);
  // Der Dialog gibt den Fokus ab; das Ziel ist wieder "Exportieren" (per Effekt, nach dem Aushaengen).
  const fokusAufExport = useRef(false);
  useEffect(() => {
    if (fokusAufExport.current) {
      fokusAufExport.current = false;
      document.getElementById(EXPORT_BUTTON_ID)?.focus();
    }
  });
  const [filterFehler, setFilterFehler] = useState<AddressFilterProblems>({});
  /** Das Feld mit dem Formfehler; es bekommt den Fokus, sobald der Bereich offen ist. */
  const fokusFilterFeld = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (fokusFilterFeld.current === undefined || !filterOffen) {
      return;
    }
    const ziel = fokusFilterFeld.current;
    fokusFilterFeld.current = undefined;
    document.getElementById(ziel)?.focus();
  });
  const [treffer, setTreffer] = useState<{ readonly zahl?: number; readonly gedeckelt: boolean }>({ gedeckelt: false });
  const optionen = useFilterOptionen(apiBaseUrl, accessToken, onReauthenticate);
  const sucheAktiv = aktiverBegriff !== undefined || aktiverFilter !== undefined;
  // Eine Seite, zwei Lagen (L13-01): ohne Suchgrund die Liste der zuletzt
  // angelegten Kontakte mit Cursor, mit Suchgrund die Treffer.
  const modus: Modus = sucheAktiv ? 'suche' : 'alle';
  // Die Art der Liste "Alle Kontakte" (Umschalter); in der Suche traegt der Filter sie.
  const [listenArt, setListenArt] = useState<PartyKindFilter | undefined>(undefined);
  const gewaehlteArt = modus === 'alle' ? listenArt : aktiverFilter?.kind;
  const artWaehlen = (wahl: string): void => {
    const art: PartyKindFilter | undefined = wahl === 'person' || wahl === 'organization' ? wahl : undefined;
    if (art === gewaehlteArt) return;
    if (modus === 'alle') {
      auswahlFuerNeuesErgebnis(undefined, undefined, art);
      setStatus('laedt');
      setListenArt(art);
      return;
    }
    // In der Suche eine Ansicht des Ergebnisses: dieselbe Suche, eingeengt.
    const next: SearchFilter = { ...(aktiverFilter ?? EMPTY_SEARCH_FILTER), kind: art };
    setEntwurf((vorher) => ({ ...vorher, kind: art }));
    rememberSearchFilter(next);
    auswahlFuerNeuesErgebnis(aktiverBegriff, next);
    setStatus('laedt');
    setAktiverFilter(next);
  };
  // Die Kurzform der Ergebniszeile (DESIGN F4): mit Trefferzahl der Suche die
  // Gesamtzahl, sonst die geladenen Zeilen und „weitere vorhanden“.
  const gesamtZahl = sucheAktiv && treffer.zahl !== undefined ? treffer.zahl : parties.length;
  const weitereVorhanden = cursor !== undefined && !(sucheAktiv && treffer.zahl !== undefined);
  /** Die Trefferzahl in Worten fuer Export und Sammlung -- beide Dialoge sagen dieselbe Zahl. */
  const trefferWorte = treffer.zahl === undefined ? '' : treffer.gedeckelt ? 'mehr als 1.000 Kontakte' : treffer.zahl === 1 ? '1 Kontakt' : `${treffer.zahl.toLocaleString('de-DE')} Kontakte`;
  // Ein gemerkter Filter mit Stichwort oder Haus braucht deren Namen fuer die
  // Chips, auch wenn der Bereich nie geoeffnet wird. Nur aus dem Ruhezustand:
  // nach einer Ablehnung laedt allein "Filter erneut laden", sonst erzeugte
  // jede Ablehnung den naechsten Abruf.
  const brauchtNamen = aktiverFilter !== undefined && (aktiverFilter.taxonomyTermIds.length > 0 || aktiverFilter.operatingContextId !== undefined);
  const optionenLaden = optionen.laden;
  const optionenRuhen = optionen.status === 'ruht';
  useEffect(() => {
    if (brauchtNamen && optionenRuhen) {
      void optionenLaden();
    }
  }, [brauchtNamen, optionenRuhen, optionenLaden]);
  const [eingabe, setEingabe] = useState<string>(gemerkterBegriff ?? '');
  const [feldfehler, setFeldfehler] = useState<string | undefined>(undefined);
  // --- Vorschlaege beim Tippen (PQL S2b) -------------------------------------
  // Steht der Cursor im Wert von `stichwort:`, fragt die Liste S0. Entprellt,
  // jede neue Eingabe bricht den vorigen Abruf ab, und je Praefix merkt sich
  // die Seite die Antwort, solange sie offen ist. Felder, Werte von Rolle und
  // Zustand, ODER und „Meintest du?“ kommen ohne Abruf (vorschlagLage).
  const [cursorStelle, setCursorStelle] = useState(0);
  const [vorschlagGeschlossen, setVorschlagGeschlossen] = useState(false);
  // Pfeil runter hat die Liste verlangt: dann auch Felder unter drei Zeichen und ODER.
  const [vorschlagVerlangt, setVorschlagVerlangt] = useState(false);
  // Tab uebernimmt nur, solange seit der letzten Uebernahme getippt wurde.
  const [getipptSeitWahl, setGetipptSeitWahl] = useState(false);
  const [tabAnsage, setTabAnsage] = useState('');
  const lage = useMemo(() => vorschlagLage(eingabe, cursorStelle, vorschlagVerlangt), [eingabe, cursorStelle, vorschlagVerlangt]);
  const wertAnfrage = lage?.art === 'stichwort' ? lage.anfrage : undefined;
  const vorschlagSpeicher = useRef(new Map<string, readonly TermSuggestion[]>());
  // Der Speicher gilt fuer eine Anmeldung an einer Adresse; wechselt eines, ist er leer.
  useEffect(() => {
    vorschlagSpeicher.current = new Map();
  }, [accessToken, apiBaseUrl]);
  // Die Feldliste des Servers (PQL S3): Namen und Aliase fuer Parser und
  // Vorschlaege. Bis sie da ist und wenn sie nicht kommt, gilt Fassung 1.
  useEffect(() => {
    let aktiv = true;
    void (async () => {
      const token = await accessToken();
      if (aktiv && token !== undefined) await feldlisteLaden({ baseUrl: apiBaseUrl, accessToken: token });
    })();
    return () => {
      aktiv = false;
    };
  }, [accessToken, apiBaseUrl]);
  const vorschlaegeAbrufen = useCallback(
    async (praefix: string, signal: AbortSignal): Promise<readonly TermSuggestion[]> => {
      const schluessel = praefix.toLocaleLowerCase('de-DE');
      const gemerkt = vorschlagSpeicher.current.get(schluessel);
      if (gemerkt !== undefined) return gemerkt;
      const token = await accessToken();
      if (token === undefined || signal.aborted) return [];
      const antwort = await fetchTermSuggestions({ baseUrl: apiBaseUrl, accessToken: token, query: praefix, signal });
      // Eine Stoerung zeigt keine Liste; beim Abschicken loest S1 den Wert ohnehin auf.
      const liste = antwort.outcome === 'loaded' ? antwort.suggestions : [];
      if (antwort.outcome === 'loaded') vorschlagSpeicher.current.set(schluessel, liste);
      return liste;
    },
    [accessToken, apiBaseUrl],
  );
  const vorschlagStand = useEntprellterAbruf({ anfrage: wertAnfrage?.praefix, verzoegerungMs: VORSCHLAG_VERZOEGERUNG_MS, abrufen: vorschlaegeAbrufen });
  const stichwortBegriffe: readonly TermSuggestion[] =
    wertAnfrage !== undefined && (vorschlagStand.zustand === 'fertig' || vorschlagStand.zustand === 'laedt') ? (vorschlagStand.antwort ?? []) : KEINE_BEGRIFFE;
  const vorschlaege = useMemo<readonly Vorschlag[]>(() => {
    if (lage === undefined) return [];
    if (lage.art === 'liste') {
      return lage.optionen.map((option) => ({ id: option.id, titel: option.titel, ...(option.zusatz === undefined ? {} : { zusatz: option.zusatz }), uebernehmen: () => ersetzen(eingabe, option.ersetzung) }));
    }
    return stichwortBegriffe.map((begriff) => ({
      id: begriff.taxonomyTermId,
      titel: begriff.displayName,
      zusatz: begriff.taxonomyDisplayName,
      uebernehmen: () => vorschlagUebernehmen(eingabe, lage.anfrage, begriff, stichwortBegriffe),
    }));
  }, [lage, stichwortBegriffe, eingabe]);
  const vorschlagOffen = !vorschlagGeschlossen && vorschlaege.length > 0;
  const vorschlagCombobox = useCombobox({
    optionen: vorschlaege,
    schluessel: (vorschlag) => vorschlag.id,
    idPraefix: 'kontakte-vorschlag-',
    listId: VORSCHLAG_LISTE_ID,
    offen: vorschlagOffen,
    // Enter sucht, solange keine Option per Pfeil aktiv ist: „Suchen“ bleibt die
    // Hauptaktion. Hat Pfeil runter die Liste geoeffnet, ist die erste aktiv.
    ersteIstAktiv: vorschlagVerlangt,
    // Vorgemerkt wird nur aus der Antwort auf das, was jetzt dasteht: waehrend ein
    // neues Praefix laedt, zeigt die Liste noch die vorige (Codex an #639).
    vormerken: getipptSeitWahl && eingabe.trim() !== '' && (wertAnfrage === undefined || vorschlagStand.zustand === 'fertig'),
    onWaehlen: (vorschlag, weg) => {
      const neu = vorschlag.uebernehmen();
      setVorschlagVerlangt(false);
      setGetipptSeitWahl(false);
      if (weg === 'tab') {
        // Nach Tab ist die Liste zu, auch wenn die Uebernahme neue Optionen hat
        // (rolle: -> Rollen); Pfeil runter oeffnet sie wieder (DESIGN C+).
        setVorschlagGeschlossen(true);
        setTabAnsage(`„${vorschlag.titel}“ übernommen.`);
      }
      setEingabe(neu.text);
      setCursorStelle(neu.cursor);
      cursorZiel.current = { from: neu.cursor, to: neu.cursor };
    },
    // Escape schliesst nur die Liste; der Text bleibt (DESIGN P2-2).
    onEscape: () => {
      setVorschlagGeschlossen(true);
      setVorschlagVerlangt(false);
      setTabAnsage('');
    },
  });
  // Die Vormerkung ist nur optisch; hoerbar wird sie hier, entprellt und nur,
  // wenn sich die vorgemerkte Option aendert.
  const vorgemerktTitel = vorschlagCombobox.vorgemerkt?.titel;
  useEffect(() => {
    if (vorgemerktTitel === undefined) {
      // Verschwindet die Vormerkung, verschwindet ihr Satz; „… übernommen.“ bleibt stehen.
      setTabAnsage((satz) => (satz.startsWith('Tab übernimmt') ? '' : satz));
      return undefined;
    }
    const zeitgeber = setTimeout(() => setTabAnsage(`Tab übernimmt „${vorgemerktTitel}“.`), VORSCHLAG_VERZOEGERUNG_MS);
    return () => clearTimeout(zeitgeber);
  }, [vorgemerktTitel]);
  /**
   * Der Hinweis zu einer Angabe im Suchtext (PQL S1): Satz mit Eingabeteilen,
   * hoechstens drei Knoepfe „… übernehmen“ und die Stelle fuer den Cursor.
   */
  const [angabenHinweis, setAngabenHinweis] = useState<
    { readonly parts: readonly MessagePart[]; readonly knoepfe: readonly { readonly label: string; readonly neu: string | undefined }[]; readonly span: Span | undefined } | undefined
  >(undefined);
  /** Was beim letzten Abschicken aus dem Suchtext in die Filter gewandert ist; die Statusregion sagt es an. */
  const [uebernommen, setUebernommen] = useState<string | undefined>(undefined);
  /** Die Stelle, an die der Cursor nach einem Hinweis springt -- per Effekt, nach dem Rendern. */
  const cursorZiel = useRef<Span | undefined>(undefined);
  /** Nur die juengste Pruefung mit Angaben darf Feld und Filter setzen; Tippen und jede neue Suche ueberholen sie. */
  const angabenLauf = useRef(0);
  useEffect(() => {
    const ziel = cursorZiel.current;
    if (ziel === undefined) return;
    cursorZiel.current = undefined;
    const feld = document.getElementById(SEARCH_INPUT_ID) as HTMLInputElement | null;
    feld?.focus();
    feld?.setSelectionRange(ziel.from, ziel.to);
  });
  /**
   * Die Laufnummer des juengsten Abrufs.
   *
   * Suche und Nachladen laufen asynchron, und "Suchen" bleibt waehrend eines
   * Nachladens erreichbar. Ohne Laufnummer koennte eine SPAETER eintreffende
   * aeltere Antwort die Treffer eines frueheren Abrufs anzeigen --
   * dieselbe Fehlerklasse wie A13 beim Detailabruf, hier als Hinweis des
   * Codex-Erstreviews vom 03.09.2026. Eine Antwort, deren Nummer nicht mehr
   * die aktuelle ist, wird verworfen.
   */
  const laufnummer = useRef(0);

  // --- Massenpflege: Auswahl und Panel (ADR-0058 Ziffer 13) -------------------
  // Die Art von "Alle Kontakte" gehoert zur Herkunft wie der Filter zur Suche:
  // ein Wechsel ist ein anderes Ergebnis, mit Ansage.
  const herkunft = JSON.stringify([modus, aktiverBegriff ?? null, aktiverFilter ?? null, modus === 'alle' ? listenArt ?? null : null]);
  const [start] = useState(() => recallSelection(herkunft));
  const [auswahl, setAuswahl] = useState<readonly SelectedParty[]>(start.parties);
  const [panelOffen, setPanelOffen] = useState(false);
  const [operation, setOperation] = useState<BatchOperation>('assign');
  const [begriffe, setBegriffe] = useState<readonly AssignableTermView[]>([]);
  const [begriffeStatus, setBegriffeStatus] = useState<'ruht' | 'laedt' | 'geladen' | 'abgelehnt'>('ruht');
  const [begriffeGekuerzt, setBegriffeGekuerzt] = useState(false);
  const [begriff, setBegriff] = useState<string | undefined>(undefined);
  // Ein gesuchtes Stichwort steht nicht zwingend in der ersten Seite: Name und Liste reisen mit.
  const [begriffGesucht, setBegriffGesucht] = useState<StichwortEintrag | undefined>(undefined);
  const [panelFehler, setPanelFehler] = useState<PlainMessage | undefined>(undefined);
  const [begriffFehler, setBegriffFehler] = useState<string | undefined>(undefined);
  const [panelBusy, setPanelBusy] = useState(false);

  /** Wie viele Kontakte ein Ergebniswechsel aus der Auswahl genommen hat; die Statusregion sagt es an. */
  const [aufgehoben, setAufgehoben] = useState<{ readonly zahl: number; readonly grund: 'ergebnis' | 'zeilen' }>({
    zahl: start.discarded,
    grund: 'ergebnis',
  });

  useEffect(() => {
    // Die Auswahl eines anderen Ergebnisses ist mit dem Aufbau aufgehoben,
    // nicht erst mit der naechsten Auswahl -- sonst kaeme sie auf dem Rueckweg
    // wieder, obwohl der Satz sie aufgehoben nannte.
    if (start.discarded > 0) rememberSelection(herkunft, []);
    // Nur beim Aufbau: danach gilt `auswaehlen`.
  }, [start]);

  /**
   * "Nur ausgewählte anzeigen" (Plan F3, Schnitt 0): eine Ansicht der geladenen
   * Zeilen, keine neue Abfrage. Sie haelt fest, welche Zeilen beim Einschalten
   * gewaehlt waren: wer darin einen Haken abnimmt, sieht die Zeile weiter --
   * sonst verschwaende sie unter dem Fokus (WCAG 2.4.3).
   */
  const [nurAnsicht, setNurAnsicht] = useState<ReadonlySet<string> | undefined>(undefined);
  const nurAusgewaehlte = nurAnsicht !== undefined;

  const auswaehlen = (
    next: readonly SelectedParty[],
    fuer: string = herkunft,
    weg: { readonly zahl: number; readonly grund: 'ergebnis' | 'zeilen' } = { zahl: 0, grund: 'ergebnis' },
  ): void => {
    setAuswahl(next);
    setAufgehoben(weg);
    // Ohne Auswahl gibt es nichts zu zeigen; der naechste Haken beginnt wieder mit allen Zeilen.
    if (next.length === 0) setNurAnsicht(undefined);
    rememberSelection(fuer, next);
  };

  /** Ein anderes Ergebnis beginnt ohne Auswahl; dieselbe Suche noch einmal ist dasselbe Ergebnis und behaelt sie. */
  const auswahlFuerNeuesErgebnis = (begriff: string | undefined, filter: SearchFilter | undefined, art?: PartyKindFilter): void => {
    const neuerModus: Modus = begriff !== undefined || filter !== undefined ? 'suche' : 'alle';
    const neu = JSON.stringify([neuerModus, begriff ?? null, filter ?? null, neuerModus === 'alle' ? art ?? null : null]);
    if (neu === herkunft) return;
    setPanelOffen(false);
    auswaehlen([], neu, { zahl: auswahl.length, grund: 'ergebnis' });
  };

  const panelOeffnen = async (): Promise<void> => {
    setPanelOffen(true);
    setPanelFehler(undefined);
    if (begriffeStatus === 'geladen') return;
    setBegriffeStatus('laedt');
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const result = await fetchAssignableTerms({ baseUrl: apiBaseUrl, accessToken: token });
      if (result.outcome === 'rejected') {
        setBegriffeStatus('abgelehnt');
        setPanelFehler(result.message);
        return;
      }
      setBegriffe(result.terms);
      // Die gekuerzte Liste wird GESAGT, nicht still verworfen -- derselbe
      // Satz wie bei der Einzelvergabe (TX-021; Codex-Cloud-Review #270, P2).
      setBegriffeGekuerzt(result.truncated);
      setBegriffeStatus('geladen');
    } catch {
      setBegriffeStatus('abgelehnt');
      setPanelFehler(UNREACHABLE_MESSAGE);
    }
  };

  // Der Oeffner verschwindet mit dem Oeffnen; der Fokus folgt erst nach dem Rendern: waehrend
  // des Ladens und bei einem Fehler auf die Statusregion, danach auf die Ueberschrift des
  // Abschnitts -- nie auf BODY. Nicht ins Feld: das Suchfeld oeffnete bei Fokus seine Liste
  // ueber "Vorschau zeigen" (DESIGN).
  useEffect(() => {
    if (!panelOffen) return;
    const ziel = begriffeStatus === 'geladen' ? PANEL_TITLE_ID : PANEL_STATUS_REGION_ID;
    document.getElementById(ziel)?.focus();
  }, [panelOffen, begriffeStatus]);

  const stichwortSuchen = useCallback(
    (getippt: string): Promise<StichwortTreffer> =>
      searchAssignableEntries({ baseUrl: apiBaseUrl, accessToken, onReauthenticate, query: getippt, ersteSeite: begriffe, ersteSeiteGekuerzt: begriffeGekuerzt, send: LESEN }),
    [apiBaseUrl, accessToken, onReauthenticate, begriffe, begriffeGekuerzt],
  );
  const begriffEintrag: StichwortEintrag | undefined = useMemo(() => {
    if (begriff === undefined) return undefined;
    const geladen = begriffe.find((eintrag) => eintrag.taxonomyTermId === begriff);
    return geladen === undefined ? (begriffGesucht?.id === begriff ? begriffGesucht : undefined) : alsEintrag(geladen);
  }, [begriff, begriffe, begriffGesucht]);

  const vorschauZeigen = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (exceedsBatch(auswahl.length)) return;
    if (begriff === undefined) {
      setBegriffFehler('Welches Stichwort möchtest du pflegen?');
      document.getElementById(PANEL_TERM_INPUT_ID)?.focus();
      return;
    }
    setBegriffFehler(undefined);
    setPanelFehler(undefined);
    setPanelBusy(true);
    try {
      const token = await accessToken();
      if (token === undefined) {
        onReauthenticate();
        return;
      }
      const request = { operation, taxonomyTermId: begriff, partyIds: auswahl.map((party) => party.partyId) };
      const result = await createBatch({ baseUrl: apiBaseUrl, accessToken: token, ...request });
      if (result.outcome === 'rejected') {
        // Die Auswahl bleibt stehen (NFR-UX-005): nichts ist verloren.
        setPanelFehler(result.message);
        document.getElementById(PANEL_STATUS_REGION_ID)?.focus();
        return;
      }
      rememberBatchRequest(result.classificationChangeBatchId, request);
      // Die Vorschau IST der naechste Bildschirm: der angelegte Lauf mit
      // Zaehlern und genau einer Hauptaktion (ADR-0058 Ziffer 2).
      auswaehlen([]);
      navigate(massenlaufHref(result.classificationChangeBatchId));
    } catch {
      setPanelFehler(PREVIEW_UNCERTAIN_MESSAGE);
      document.getElementById(PANEL_STATUS_REGION_ID)?.focus();
    } finally {
      setPanelBusy(false);
    }
  };

  const gewaehlt = new Set(auswahl.map((party) => party.partyId));
  const seite: readonly SelectedParty[] = parties.map((party) => ({ partyId: party.partyId, displayName: party.displayName }));
  const seiteGanz = seite.length > 0 && seite.every((party) => gewaehlt.has(party.partyId));
  // Die Auswahl hat keine Grenze; nur die Pflege bleibt bei MAXIMUM_BATCH_PARTIES
  // (ADR-0058) und sagt darueber, warum nicht.
  const zuGrossFuerPflege = exceedsBatch(auswahl.length);
  /**
   * Massenaktionen nur auf einem geladenen Ergebnis, dessen Zeilen JEDEN
   * Ausgewaehlten zeigen -- auch im einen Render, bevor der Abgleich unten
   * greift. Laedt es noch oder ist der Abruf gescheitert, bleibt die Auswahl
   * stehen (nichts verloren), angeboten wird aber nur "Suchen" -- sonst
   * gingen Kontakte in einen Lauf, die der Bildschirm gerade nicht zeigt.
   */
  const geladeneZeilen = new Set(parties.map((party) => party.partyId));
  const auswahlBereit = auswahl.length > 0 && status === 'geladen' && auswahl.every((party) => geladeneZeilen.has(party.partyId));

  const load = useCallback(
    async (begriff: string | undefined, filter: SearchFilter | undefined, weiter: string | undefined, art?: PartyKindFilter) => {
      const meine = ++laufnummer.current;
      const veraltet = (): boolean => meine !== laufnummer.current;
      setBusy(true);
      try {
        const token = await accessToken();
        if (token === undefined) {
          // Kein Fehler, sondern eine abgelaufene Anmeldung -- dieselbe
          // Behandlung wie in der Schnellerfassung.
          onReauthenticate();
          return;
        }
        const result =
          begriff === undefined && filter === undefined
            ? await fetchPartyList({
                baseUrl: apiBaseUrl,
                accessToken: token,
                send: LESEN,
                ...(weiter === undefined ? {} : { cursor: weiter }),
                ...(art === undefined ? {} : { kind: art }),
              })
            : await searchParties({
                baseUrl: apiBaseUrl,
                accessToken: token,
                send: LESEN,
                term: begriff,
                filter,
                ...(weiter === undefined ? {} : { cursor: weiter }),
              });
        if (veraltet()) {
          return;
        }
        if (result.outcome === 'rejected') {
          // Eine gescheiterte ERSTE Seite (neue Suche oder neue Liste) laesst
          // keine alten Zeilen und keinen alten Cursor stehen: sonst zeigte
          // der Bildschirm fremde Treffer unter dem neuen Begriff, und
          // "Weitere Kontakte laden" schickte den neuen Begriff mit dem
          // alten Cursor (Befund des Cloud-Reviews an #228). Eine gescheiterte
          // FOLGESEITE behaelt beides -- die Nutzerin soll ihre Stelle nicht
          // verlieren.
          if (weiter === undefined) {
            setParties([]);
            setCursor(undefined);
          }
          setStatus('abgelehnt');
          setMessage(result.message);
          setFehlgeschlagenerCursor(weiter);
          return;
        }
        // ANHAENGEN und nicht ersetzen, wenn ein Cursor im Spiel war: eine
        // Folgeseite, die die vorherige verwirft, laesst die Nutzerin ihren
        // gerade gelesenen Kontakt verlieren.
        setParties((vorher) => (weiter === undefined ? result.parties : [...vorher, ...result.parties]));
        // Die Zahl kommt nur mit der ersten Seite; eine Folgeseite laesst sie stehen.
        if (weiter === undefined) {
          const zahl = 'matchCount' in result && typeof result.matchCount === 'number' ? result.matchCount : undefined;
          const gedeckelt = 'matchCountCapped' in result && result.matchCountCapped === true;
          setTreffer(zahl === undefined ? { gedeckelt: false } : { zahl, gedeckelt });
        }
        setCursor(result.nextCursor);
        setMessage(undefined);
        setFehlgeschlagenerCursor(undefined);
        setStatus('geladen');
      } catch {
        if (veraltet()) {
          return;
        }
        if (weiter === undefined) {
          setParties([]);
          setCursor(undefined);
        }
        // Eine Stoerung des Netzes ist keine Ablehnung. Derselbe Rueckfall wie
        // in der Schnellerfassung: Status 0, keine Antwort.
        setStatus('abgelehnt');
        setMessage({
          headline: 'LIAISON ist gerade nicht erreichbar.',
          explanation: 'Die Kontakte ließen sich nicht laden.',
          nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
          retryable: true,
        });
        setFehlgeschlagenerCursor(weiter);
      } finally {
        if (!veraltet()) {
          setBusy(false);
        }
      }
    },
    [accessToken, apiBaseUrl, onReauthenticate],
  );

  useEffect(() => {
    // Ohne Suchgrund die Liste in der gewaehlten Art, sonst die Treffer.
    void load(aktiverBegriff, aktiverFilter, undefined, aktiverBegriff === undefined && aktiverFilter === undefined ? listenArt : undefined);
    // Beim Mount und beim Wechsel des aktiven Begriffs oder der Art -- der
    // Text im Feld gehoert absichtlich nicht dazu.
  }, [load, aktiverBegriff, aktiverFilter, listenArt]);

  useEffect(() => {
    // Dieselbe Suche kann beim erneuten Abruf andere Treffer liefern, und der
    // Rueckweg aus dem Kontakt laedt nur die erste Seite: wer dann nicht mehr
    // in der Liste steht, verlaesst die Auswahl -- mit Ansage. Nachladen
    // erweitert die Liste und nimmt nichts heraus.
    if (status !== 'geladen') return;
    const sichtbar = new Set(parties.map((party) => party.partyId));
    const bleibt = auswahl.filter((party) => sichtbar.has(party.partyId));
    if (bleibt.length < auswahl.length) auswaehlen(bleibt, herkunft, { zahl: auswahl.length - bleibt.length, grund: 'zeilen' });
    // Nur ein neues Ergebnis prueft; ein Haken allein aendert die Liste nicht.
  }, [parties, status]);

  /**
   * Der Fokus NACH dem Nachladen oder Suchen -- und warum er in einem Effekt
   * steht.
   *
   * Die erste Fassung setzte ihn im `then` des Abrufs. Das ist zu frueh: der
   * Zustand ist dann gesetzt, aber React hat noch nicht neu gerendert. Der
   * Knopf existierte in diesem Moment noch, bekam den Fokus -- und wurde
   * unmittelbar danach entfernt, weil es keine weitere Seite mehr gab. Der
   * Fokus fiel auf das Dokument zurueck, und die Probe A11 hat genau das
   * gemessen. Ein Effekt laeuft NACH dem Rendern und sieht den Bildschirm, den
   * die Nutzerin sieht.
   *
   * Das Ziel steht im Ref, nicht im Zustand: es soll den Fokus setzen, aber
   * kein Rendern ausloesen.
   */
  const fokusNachLaden = useRef<'knopf' | 'status' | 'feld' | undefined>(undefined);

  useEffect(() => {
    if (fokusNachLaden.current === undefined || busy || status === 'laedt') {
      return;
    }
    const ziel = fokusNachLaden.current;
    fokusNachLaden.current = undefined;
    // Nach dem Nachladen der Knopf, wenn es ihn noch gibt; sonst und nach
    // einer Suche die Statusregion. Sie sagt in genau diesem Moment, was die
    // Suche ergeben hat, und ist deshalb das richtige Ziel (WCAG 2.4.3,
    // NFR-ACC-001).
    // Nach einer Suche bleibt der Fokus im Feld; die Statusregion sagt das
    // Ergebnis an (aria-live). So kann man sofort weiter eingrenzen (DESIGN,
    // vorgabe-pql-s1 Abschnitt 4, fuer jede Suche).
    if (ziel === 'feld') {
      const feld = document.getElementById(SEARCH_INPUT_ID);
      if (feld !== null) {
        feld.focus();
        return;
      }
    }
    const knopf = ziel === 'knopf' ? document.getElementById(MORE_BUTTON_ID) : null;
    (knopf ?? document.getElementById(STATUS_REGION_ID))?.focus();
  });

  const weiterLaden = (): void => {
    fokusNachLaden.current = 'knopf';
    void load(aktiverBegriff, aktiverFilter, cursor, listenArt);
  };

  /** Startet eine Suche mit Begriff und Filtern; gemeinsamer Schluss beider Wege. */
  const sucheStarten = (begriff: string | undefined, filter: SearchFilter): void => {
    rememberSearchTerm(begriff);
    rememberSearchFilter(filter);
    auswahlFuerNeuesErgebnis(begriff, filter);
    fokusNachLaden.current = 'feld';
    setStatus('laedt');
    if (begriff === aktiverBegriff && JSON.stringify(filter) === JSON.stringify(aktiverFilter)) {
      // Dieselbe Suche noch einmal: der Effekt oben feuert nicht, also
      // ausdruecklich neu laden -- der Bestand kann sich geaendert haben.
      void load(begriff, filter, undefined);
      return;
    }
    setAktiverBegriff(begriff);
    setAktiverFilter(filter);
  };

  /**
   * Suchtext mit Angaben (PQL S1, E2b a): pruefen, Stichwoerter und Haus
   * aufloesen, dann wandern die Angaben in die Filter und im Feld bleibt der
   * Freitext. Ein Hinweis laesst die Eingabe stehen und setzt den Cursor an
   * die Stelle; gesendet wird dann nichts.
   */
  const mitAngabenSuchen = async (text: string): Promise<void> => {
    const meiner = ++angabenLauf.current;
    setFeldfehler(undefined);
    setFilterFehler({});
    setUebernommen(undefined);
    const geprueft = checkForS1(text);
    // Ein kleines „oder“ hinter einer Angabe sperrt hier, obwohl der Parser es
    // als Freitext liest: sonst suchte die Liste still etwas anderes, als
    // gemeint war (Codex P1 zu #510).
    const sperrend = geprueft.hints.find((hint) => hint.blocking || hint.code === 'lowercase_operator');
    if (geprueft.form === undefined || sperrend !== undefined) {
      const hint = sperrend ?? geprueft.hints[0];
      if (hint !== undefined) {
        const satz = hintMessage(hint);
        setAngabenHinweis({
          parts: satz.parts,
          knoepfe: satz.action === undefined ? [] : [{ label: satz.action.label, neu: applyAction(text, satz.action) }],
          span: hint.span,
        });
        cursorZiel.current = hint.span;
      }
      return;
    }
    const form = geprueft.form;
    const nochmal = { parts: [{ kind: 'text' as const, text: 'Die Stichworte ließen sich gerade nicht prüfen. Deine Eingabe bleibt stehen.' }], knoepfe: [{ label: 'Erneut versuchen', neu: undefined }], span: undefined };
    let zugang: string | undefined;
    try {
      zugang = await accessToken();
    } catch {
      if (meiner !== angabenLauf.current) return;
      setAngabenHinweis(nochmal);
      return;
    }
    if (meiner !== angabenLauf.current) return;
    if (zugang === undefined) {
      onReauthenticate();
      return;
    }
    setBusy(true);
    let ergebnis;
    try {
      ergebnis = await resolveS1Form(form, {
        suggestions: (query) => fetchTermSuggestions({ baseUrl: apiBaseUrl, accessToken: zugang, query }),
        houses: async () => {
          const antwort = await fetchSearchOptions({ baseUrl: apiBaseUrl, accessToken: zugang }).catch(() => undefined);
          if (antwort?.outcome !== 'loaded') return undefined;
          uebernimmHausArten(antwort.operatingContexts.map((haus) => haus.kind));
          return antwort.operatingContexts;
        },
      });
    } finally {
      setBusy(false);
    }
    if (meiner !== angabenLauf.current) return;
    if (ergebnis.outcome === 'unavailable') {
      setAngabenHinweis(nochmal);
      document.getElementById(SEARCH_INPUT_ID)?.focus();
      return;
    }
    if (ergebnis.outcome === 'hint') {
      setAngabenHinweis({
        parts: ergebnis.hint.parts,
        knoepfe: ergebnis.hint.actions.map((action) => ({ label: action.label, neu: applyAction(text, action) })),
        span: ergebnis.hint.span,
      });
      cursorZiel.current = ergebnis.hint.span;
      return;
    }
    const aufgeloest = ergebnis.resolved;
    // Stichwoerter verbinden sich mit den gesetzten per ODER (Regel der
    // Facette, „eines reicht“); Haus, PLZ und Ort ersetzen den gesetzten Wert.
    const stichwoerter = [...entwurf.taxonomyTermIds, ...aufgeloest.taxonomyTermIds.filter((id) => !entwurf.taxonomyTermIds.includes(id))];
    if (stichwoerter.length > MAXIMUM_FILTER_TERMS) {
      setAngabenHinweis({
        parts: [{ kind: 'text', text: `Eine Suche nimmt höchstens ${MAXIMUM_FILTER_TERMS} Stichworte. Entferne eines im Filter oder im Suchtext.` }],
        knoepfe: [],
        span: undefined,
      });
      return;
    }
    const next: SearchFilter = {
      ...entwurf,
      taxonomyTermIds: stichwoerter,
      termNames: { ...entwurf.termNames, ...aufgeloest.termNames },
      operatingContextId: aufgeloest.operatingContextId ?? entwurf.operatingContextId,
      roleCodes: [...entwurf.roleCodes, ...aufgeloest.roleCodes.filter((code) => !entwurf.roleCodes.includes(code))],
      postalCodePrefix: aufgeloest.postalCodePrefix ?? entwurf.postalCodePrefix,
      locality: aufgeloest.locality ?? entwurf.locality,
      includeInactive: aufgeloest.excludeInactive ? false : entwurf.includeInactive,
    };
    // PLZ und Ort aus dem Filterbereich, die der Suchtext nicht ersetzt, gelten
    // weiter; ein Formfehler dort steht am Feld wie beim gewohnten Suchen.
    const anschrift = addressFilterProblems(next);
    if (anschrift.postalCodePrefix !== undefined || anschrift.locality !== undefined) {
      setFilterFehler(anschrift);
      fokusFilterFeld.current = anschrift.postalCodePrefix !== undefined ? FILTER_POSTAL_CODE_ID : FILTER_LOCALITY_ID;
      setFilterOffen(true);
      void optionen.laden();
      return;
    }
    setAngabenHinweis(undefined);
    // Der Chip des Hauses und der Kopiertext brauchen die Namen der Auswahl.
    if (aufgeloest.operatingContextId !== undefined) void optionen.laden();
    setEntwurf(next);
    setEingabe(aufgeloest.freeText ?? '');
    setUebernommen(uebernommenSatz(aufgeloest, form.houseName));
    sucheStarten(aufgeloest.freeText, next);
  };

  const suchen = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    // Nach dem Abschicken laege die Liste ueber Hinweis und Knoepfen darunter.
    setVorschlagGeschlossen(true);
    setTabAnsage('');
    angabenLauf.current += 1;
    if (usesQuerySyntax(eingabe)) {
      void mitAngabenSuchen(eingabe);
      return;
    }
    setAngabenHinweis(undefined);
    setUebernommen(undefined);
    // PLZ und Ort zuerst: ein Formfehler dort steht am Feld, der Bereich geht
    // dafuer auf, und gesendet wird nichts (NFR-UX-005).
    const anschrift = addressFilterProblems(entwurf);
    if (anschrift.postalCodePrefix !== undefined || anschrift.locality !== undefined) {
      setFeldfehler(undefined);
      setFilterFehler(anschrift);
      fokusFilterFeld.current = anschrift.postalCodePrefix !== undefined ? FILTER_POSTAL_CODE_ID : FILTER_LOCALITY_ID;
      setFilterOffen(true);
      void optionen.laden();
      return;
    }
    setFilterFehler({});
    // Ohne Begriff genuegt ein Suchgrund im Filter (Stichwort, Haus, Rolle,
    // PLZ oder Ort) -- dieselbe Regel wie der Server. "Ohne Inaktive" allein
    // ist keiner.
    const ohneBegriff = eingabe.trim().length === 0 && filterNarrowsSearch(entwurf);
    if (eingabe.trim().length === 0 && !ohneBegriff && sucheAktiv) {
      // Feld geleert und kein Suchgrund mehr: zurueck zur ganzen Liste (L13-01).
      setFeldfehler(undefined);
      rememberSearchTerm(undefined);
      rememberSearchFilter(undefined);
      auswahlFuerNeuesErgebnis(undefined, undefined, listenArt);
      fokusNachLaden.current = 'feld';
      setStatus('laedt');
      setAktiverBegriff(undefined);
      setAktiverFilter(undefined);
      return;
    }
    const problem = ohneBegriff
      ? undefined
      : eingabe.trim().length === 0
      ? 'Gib mindestens drei Zeichen ein, oder grenze die Suche unter Filter ein, etwa nach Stichwort, Rolle oder PLZ.'
      : searchTermProblem(eingabe);
    if (problem !== undefined) {
      // Die Eingabe bleibt stehen; der Satz steht am Feld (NFR-UX-005).
      setFeldfehler(problem);
      document.getElementById(SEARCH_INPUT_ID)?.focus();
      return;
    }
    setFeldfehler(undefined);
    sucheStarten(ohneBegriff ? undefined : eingabe.trim(), entwurf);
  };

  /** Ein Chip entfernt: sofort neu suchen -- oder ruhen, wenn kein Suchgrund bleibt. */
  const filterEntfernen = (ohne: (filter: SearchFilter) => SearchFilter): void => {
    if (aktiverFilter === undefined) {
      return;
    }
    const next = ohne(aktiverFilter);
    setUebernommen(undefined);
    setEntwurf(ohne(entwurf));
    rememberSearchFilter(next);
    auswahlFuerNeuesErgebnis(aktiverBegriff, next);
    if (aktiverBegriff === undefined && !filterNarrowsSearch(next)) {
      rememberSearchFilter(undefined);
      setAktiverFilter(undefined);
      // Kein Suchgrund mehr: zurueck zur Liste, die der Lade-Effekt holt.
      setParties([]);
      setCursor(undefined);
      setStatus('laedt');
      document.getElementById(SEARCH_INPUT_ID)?.focus();
      return;
    }
    // Der Fokus folgt dem Ergebnis in die Statusregion -- den entfernten Chip gibt es danach nicht mehr.
    fokusNachLaden.current = 'feld';
    setStatus('laedt');
    setAktiverFilter(next);
  };

  // Der Zustand ohne Treffer (DESIGN F4): Titel, Satz und hoechstens zwei
  // Weiterwege, alle secondary -- die Hauptaktion bleibt „Suchen“.
  const leer: { readonly titel: string; readonly saetze: readonly string[]; readonly haupt?: ZustandsAktion; readonly neben?: ZustandsAktion } | undefined = (() => {
    if (status !== 'geladen' || parties.length > 0) return undefined;
    const anlegen: ZustandsAktion = { label: 'Kontakt anlegen', href: '/kontakt-anlegen' };
    const alleArten: ZustandsAktion = { label: 'Alle Arten zeigen', onClick: () => artWaehlen('alle') };
    if (!sucheAktiv) {
      return listenArt === undefined
        ? { titel: 'Noch keine Kontakte', saetze: ['Hier erscheint jeder Kontakt, den du oder deine Kolleg:innen anlegen.'], haupt: anlegen }
        : { titel: kindEmptyHeading(listenArt), saetze: [], haupt: alleArten };
    }
    const wege: ZustandsAktion[] = [];
    // Wer einen deaktivierten Kontakt sucht, soll nicht glauben, es gebe ihn nicht (E3, NFR-UX-005).
    if (aktiverFilter !== undefined && !aktiverFilter.includeInactive) {
      wege.push({ label: 'Auch inaktive zeigen', onClick: () => filterEntfernen((f) => ({ ...f, includeInactive: true })) });
    }
    if (aktiverFilter?.kind !== undefined) wege.push(alleArten);
    wege.push(anlegen);
    const satz = matchSummary(
      aktiverBegriff,
      aktiverFilter === undefined ? 0 : activeFilterCount(aktiverFilter),
      0,
      false,
      0,
      false,
      aktiverFilter !== undefined && !aktiverFilter.includeInactive,
    );
    return { titel: 'Kein Kontakt gefunden', saetze: [satz], haupt: wege[0]!, ...(wege[1] === undefined ? {} : { neben: wege[1] }) };
  })();


  const alleSpalten = useKontaktSpalten(aktiverFilter?.contactPersonOf);
  // Zeigt die Liste nur Organisationen, sagt die Spalte "Organisation" in jeder
  // Zeile "entfällt" (Rundgang 3). Sie faellt erst weg, wenn auch die Zeilen
  // so weit sind, damit beim Umschalten keine Person ohne Organisation steht.
  const nurOrganisationen = gewaehlteArt === 'organization' && parties.every((party) => party.kind === 'organization');
  // Der Tabellenname folgt der Art erst, wenn die Zeilen es tun: waehrend des Ladens stehen noch
  // die alten, und "Personen" ueber Organisationen waere vorgelesen falsch (Codex-P2 zu #769).
  const tabellenArt = gewaehlteArt !== undefined && parties.every((party) => party.kind === gewaehlteArt) ? gewaehlteArt : undefined;
  const spalten = useMemo(
    () => (nurOrganisationen ? alleSpalten.filter((spalte) => spalte.id !== 'organisation') : alleSpalten),
    [alleSpalten, nurOrganisationen],
  );

  // Der Fehler ersetzt die Hinweiszeile (DESIGN P2 zu #510).
  const hinweisSichtbar = feldfehler === undefined && angabenHinweis === undefined;
  const filterKnopf = (
    <FilterKnopf
      offen={filterOffen}
      entwurf={entwurf}
      onUmschalten={() => {
        setFilterOffen(!filterOffen);
        void optionen.laden();
      }}
      textKlasse={own.nurBreit}
      className={own.filterKnopf}
    />
  );

  return (
    <Card className={styles.card}>

      {/* DIE WERKZEUGZEILE (v4): „Liste filtern“ vor dem Umschalter der Art.
          Das Feld IST die Hauptaktion der Liste; Enter oder die Lupe im Feld
          schicken ab, einen eigenen Knopf „Suchen“ gibt es nicht mehr (DESIGN
          vorgabe-pql-s1). `noValidate`, weil die Regel hier in Worten steht und
          nicht in der Browsermeldung. KEIN Einleitungssatz davor: Beschriftung
          und Hinweis sagen, was die Suche kann, und das Feld ist so das erste
          bedienbare Element nach der Ueberschrift (F4). */}
      {/* EIN FORMULAR um Werkzeugzeile, Chips und Filterbereich: Enter in
          jedem Feld schickt dieselbe Suche ab. Alle anderen Knoepfe darin
          sind type="button". */}
      <form className={own.form} onSubmit={suchen} noValidate>
        {/* DER UMSCHALTER DER ART (v4 "Personen / Organisationen / Alle"):
            eine Ansicht des Ergebnisses, kein Suchgrund. Immer da: ohne Suche
            engt er die Liste ein, mit Suche die Treffer. Schaltergruppe nach APG
            (aria-pressed, jeder Knopf ein Tabstopp); der Fokus bleibt am Knopf,
            die Statusregion sagt das neue Ergebnis an. */}
        <div className={own.werkzeugzeile}>
          <div className={own.umschalterPlatz}>
            <Schaltergruppe label="Art der Kontakte" optionen={ART_OPTIONEN} gewaehlt={gewaehlteArt ?? 'alle'} onWahl={artWaehlen} className={own.segment} />
          </div>
          <Field
            className={own.field}
            label="Liste filtern"
            // Der Fehler ERSETZT den Hinweis, solange er steht (DESIGN P2 zu #510);
            // aria-describedby zeigt dann nur auf den Fehler.
            hint={
              hinweisSichtbar ? (
                <span className={own.hinweiszeile}>
                  <span>
                    Ab drei Zeichen. <span className={own.eingabeTeil} data-eingabe="">ort:</span>, <span className={own.eingabeTeil} data-eingabe="">stichwort:</span> und{' '}
                    <span className={own.eingabeTeil} data-eingabe="">haus:</span> werden zu Filtern.
                  </span>
                  <Suchhilfe className={own.nurBreit} />
                </span>
              ) : null
            }
            validationMessage={
              feldfehler ??
              (angabenHinweis === undefined ? null : (
                <>
                  {angabenHinweis.parts.map((teil, index) =>
                    teil.kind === 'input' ? (
                      <span key={index} className={own.eingabeTeil} data-eingabe="">
                        {teil.text}
                      </span>
                    ) : (
                      <span key={index}>{teil.text}</span>
                    ),
                  )}
                  {angabenHinweis.knoepfe.length > 0 ? (
                    <span className={own.hinweisKnoepfe}>
                      {angabenHinweis.knoepfe.map((knopf) => (
                        <Button
                          key={knopf.label}
                          type="button"
                          size="small"
                          appearance="secondary"
                          className={own.hinweisKnopf}
                          onClick={() => {
                            // Der Knopf ersetzt nur das Wort und schickt sofort ab (DESIGN, Abschnitt 2).
                            const text = knopf.neu ?? eingabe;
                            setEingabe(text);
                            void mitAngabenSuchen(text);
                          }}
                        >
                          {knopf.label}
                        </Button>
                      ))}
                    </span>
                  ) : undefined}
                </>
              ))
            }
            validationState={feldfehler === undefined && angabenHinweis === undefined ? 'none' : 'error'}
          >
            {/* Die Liste haengt an der Unterkante des Eingabefelds, nicht unter dem
                Hinweis darunter (DESIGN P2 zu #574): Field reicht die Feldkennungen
                per Context weiter, der Wrapper stoert sie nicht. */}
            <div className={own.eingabeAnker}>
              <Input
                id={SEARCH_INPUT_ID}
                type="search"
                value={eingabe}
                // Bis 500 Zeichen fuer Angaben; der Freitextanteil behaelt seine Grenze (searchTermProblem).
                maxLength={MAXIMUM_QUERY_LENGTH}
                autoComplete="off"
                // Ein freies Wort sucht nur im Namen; der Ort steht deshalb als Angabe da (A6).
                placeholder="Name, ort:Köln oder stichwort:Presse"
                // Die Lupe IST „Suchen“ in allen Breiten: per Tab direkt nach dem
                // Feld, mindestens 24 px; Enter und Lupe schicken dasselbe Formular.
                contentAfter={
                  <Button type="submit" appearance="transparent" size="small" icon={<Symbol name="suche" />} aria-label="Suchen" disabled={busy} className={own.lupe} />
                }
                input={{
                  ...vorschlagCombobox.eingabeAttribute,
                  // Der Cursor bestimmt, ob ein Wert von `stichwort:` vorne steht.
                  onSelect: (ereignis) => setCursorStelle(ereignis.currentTarget.selectionStart ?? 0),
                  // Wer das Feld verlaesst, schliesst die Liste (APG); ein Klick auf eine
                  // Option verliert den Fokus nicht (onMouseDown im Haken).
                  onBlur: () => {
                    setVorschlagGeschlossen(true);
                    setTabAnsage('');
                  },
                }}
                onKeyDown={(ereignis) => {
                  // Pfeil runter oeffnet eine geschlossene Liste (auch nach Escape).
                  if (ereignis.key === 'ArrowDown' && !vorschlagOffen && !ereignis.altKey) {
                    ereignis.preventDefault();
                    setVorschlagGeschlossen(false);
                    setVorschlagVerlangt(true);
                    return;
                  }
                  vorschlagCombobox.beiTaste(ereignis);
                  // Escape leert ein Suchfeld im Browser; eine lange Eingabe ginge
                  // so verloren. Geleert wird nur ueber das Kreuz (DESIGN P2-2).
                  if (ereignis.key === 'Escape') ereignis.preventDefault();
                }}
                onChange={(ereignis, data) => {
                  setEingabe(data.value);
                  setCursorStelle(ereignis.currentTarget.selectionStart ?? data.value.length);
                  setVorschlagGeschlossen(false);
                  setVorschlagVerlangt(false);
                  setGetipptSeitWahl(true);
                  // Wer weitertippt, verwirft die per Pfeil gewaehlte Option: Enter sucht
                  // dann wieder, statt eine alte Option einzusetzen (Codex-Review an #574).
                  vorschlagCombobox.zuruecksetzen();
                  if (feldfehler !== undefined) {
                    setFeldfehler(undefined);
                  }
                  if (angabenHinweis !== undefined) {
                    setAngabenHinweis(undefined);
                  }
                  // Wer weitertippt, ueberholt eine laufende Pruefung mit Angaben.
                  angabenLauf.current += 1;
                }}
              />
              {vorschlagOffen ? (
                <div id={VORSCHLAG_LISTE_ID} role="listbox" aria-label="Vorschläge" className={own.vorschlagListe}>
                  {vorschlaege.map((vorschlag) => (
                    <div
                      key={vorschlag.id}
                      {...vorschlagCombobox.optionAttribute(vorschlag)}
                      className={mergeClasses(own.vorschlag, vorschlag === vorschlagCombobox.vorgemerkt ? own.vorgemerkt : undefined)}
                    >
                      {/* Vorgelesen mit Pause („stichwort:Pre, Meintest du das?“); der Name
                          kommt aus dem sichtbaren Text, kein aria-label daneben (WCAG 2.5.3).
                          Das Komma steht im Titel, sonst setzt der Browser ein Leerzeichen davor. */}
                      <span>
                        {vorschlag.titel}
                        {vorschlag.zusatz === undefined ? undefined : <span className={own.kommaLeser}>,</span>}
                      </span>
                      <span className={own.vorschlagRechts}>
                        {vorschlag.zusatz === undefined ? undefined : <Caption1 className={own.vorschlagAchse}>{vorschlag.zusatz}</Caption1>}
                        {vorschlag === vorschlagCombobox.vorgemerkt ? <Tasten folge="Tab" stumm /> : undefined}
                      </span>
                    </div>
                  ))}
                </div>
              ) : undefined}
              {/* Keine eigene Ansage der Zahl (DESIGN P2-4): die Optionen tragen
                  aria-setsize/aria-posinset, Screenreader sagen „1 von 3“. Angesagt
                  wird nur, was Tab uebernimmt und was Tab uebernommen hat. Die Region
                  steht nur, solange die Liste offen ist oder etwas sagt: im Ruhezustand
                  bleibt die Statusregion der Liste die einzige. Sie entsteht leer mit der
                  Liste, der Satz kommt entprellt danach und wird so angesagt. */}
              {vorschlagOffen || tabAnsage !== '' ? (
                <div role="status" aria-live="polite" aria-label="Status: Vorschlag" className={own.nurLeser}>
                  {tabAnsage}
                </div>
              ) : undefined}
            </div>
          </Field>
          <div className={own.feldKnoepfe}>
            {/* Ersetzt ein Fehler die Hinweiszeile, steht der „?“-Knopf auch breit da (Fehlererholung). */}
            <Suchhilfe nurSymbol className={hinweisSichtbar ? own.nurSchmal : undefined} />
            {filterKnopf}
          </div>
        </div>
        {aktiverFilter !== undefined ? (
          <AktiveFilter
            filter={aktiverFilter}
            optionen={optionen}
            onEntfernen={filterEntfernen}
            suchtext={aktiverSuchtext(aktiverBegriff, aktiverFilter, optionen.begriffe, optionen.haeuser)}
            onKopierenGescheitert={(text) => {
              setEingabe(text);
              cursorZiel.current = { from: 0, to: text.length };
            }}
          />
        ) : undefined}
          {filterOffen ? (
            <div className={own.filterZeile}>
              <FilterBereich
                entwurf={entwurf}
                onEntwurf={(next) => {
                  setEntwurf(next);
                  // Wer das Feld aendert, hat den Satz gelesen; der Fehler des anderen Felds bleibt stehen.
                  setFilterFehler((alt) => ({
                    postalCodePrefix: next.postalCodePrefix === entwurf.postalCodePrefix ? alt.postalCodePrefix : undefined,
                    locality: next.locality === entwurf.locality ? alt.locality : undefined,
                  }));
                }}
                optionen={optionen}
                fehler={filterFehler}
                ungesendet={aktiverFilter !== undefined && JSON.stringify(entwurf) !== JSON.stringify(aktiverFilter)}
              />
            </div>
          ) : undefined}
      </form>

      {/* GENAU EINE Statusregion je Bildschirm -- dieselbe Regel wie in der
          Schnellerfassung. Sie traegt die Zusammenfassung UND eine Ablehnung,
          damit ein Screenreader beide an derselben Stelle hoert. */}
      {/* `tabIndex={-1}`: nicht in der Tabreihenfolge, aber programmatisch
          fokussierbar -- das Ziel nach einer Suche und das Ausweichziel nach
          der letzten Seite. */}
      {/* Seit der Massenpflege kann eine zweite Region im Bildschirm stehen
          ("Status: Stichwort pflegen"); jede traegt deshalb einen
          unterscheidenden Namen (ux_principles ST-TAX-002, Experience-Review
          28.09.2026, P2). */}


      {/* DIE ERGEBNISZEILE (v4): Zahl und Auswahl in einer
          Reihe, "3 Kontakte gefunden. · 1 Kontakt ausgewählt." Die
          Statusregion bleibt dieselbe und bleibt immer eingehaengt. */}
      <div className={own.ergebniszeile}>
      <div aria-live="polite" role="status" aria-label="Status: Kontakte" id={STATUS_REGION_ID} tabIndex={-1} className={own.statusZeile}>
        {status === 'laedt' ? (
          <Spinner
            labelPosition="after"
            label={sucheAktiv ? 'Kontakte werden gesucht' : 'Kontakte werden geladen'}
          />
        ) : undefined}
        {/* Die Region sagt den vollen Satz mit Begriff und Zahl in Worten
            (NFR-ACC-001); sichtbar steht daneben die Kurzform (DESIGN F4). */}
        {status === 'geladen' ? (
          <span className={own.nurLeser}>
            {!sucheAktiv
              ? listSummary(parties.length, cursor !== undefined)
              : matchSummary(
                  aktiverBegriff,
                  aktiverFilter === undefined ? 0 : activeFilterCount(aktiverFilter),
                  parties.length,
                  cursor !== undefined,
                  treffer.zahl,
                  treffer.gedeckelt,
                  aktiverFilter !== undefined && !aktiverFilter.includeInactive,
                )}
          </span>
        ) : undefined}
        {uebernommen !== undefined && status === 'geladen' ? <Caption1> {uebernommen}</Caption1> : undefined}
        {/* Die Einengung auf eine Art gehoert zur Aussage, nicht nur zum Haken
            am Umschalter. */}
        {gewaehlteArt !== undefined && status === 'geladen' ? <Caption1> {kindViewNotice(gewaehlteArt)}</Caption1> : undefined}
        {nurAnsicht !== undefined && status === 'geladen' ? (
          <Caption1>
            {' '}
            {/* Die Zeilen der Ansicht, nicht die Auswahl: ein abgenommener Haken laesst seine Zeile stehen. */}
            {`Die Liste zeigt ${parties.filter((party) => nurAnsicht.has(party.partyId)).length} von ${parties.length} geladenen Kontakten.`}
          </Caption1>
        ) : undefined}
        {aufgehoben.zahl > 0 && status !== 'laedt' ? (
          <Caption1> {selectionDiscardedNotice(aufgehoben.zahl, aufgehoben.grund)}</Caption1>
        ) : undefined}
        {status === 'abgelehnt' && message !== undefined ? (
          // Die Liste liess sich nicht laden: ein Lesefehler, geschrieben ist nichts.
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>{message.headline}</MessageBarTitle>
              <div>{message.explanation}</div>
              <div>{message.nextStep}</div>
            </MessageBarBody>
          </MessageBar>
        ) : undefined}
      </div>
      {/* Die sichtbare Kurzform (DESIGN F4): „12 Kontakte · weitere vorhanden“.
          Fuer Screenreader steht der volle Satz in der Region davor. Ohne
          Treffer traegt die Tabellenkarte den Zustand. */}
      {status === 'geladen' && leer === undefined ? (
        <Caption1 aria-hidden="true" className={own.kurzform}>
          {resultCountLabel(gesamtZahl, sucheAktiv && treffer.gedeckelt)}
          {weitereVorhanden ? ' · weitere vorhanden' : undefined}
        </Caption1>
      ) : undefined}
      {/* Hinter der Region, nicht in ihr: die Zeile hat ihre eigene Ansage (#534). */}
      {status === 'abgelehnt' ? <VorgangNach meldungen={[message]} /> : undefined}
      {/* Sichtbar „· 3 ausgewählt“ (DESIGN F4); der volle Satz steht verborgen
          daneben, damit der Lesecursor die Auswahl auch ohne Haken findet. */}
      {parties.length > 0 ? (
        <Caption1 className={own.auswahlSatz}>
          <span className={own.nurLeser}>{selectionSummary(auswahl.length)}</span>
          {auswahl.length > 0 ? <span aria-hidden="true">{`· ${auswahl.length.toLocaleString('de-DE')} ausgewählt`}</span> : undefined}
        </Caption1>
      ) : undefined}
      {parties.length > 0 && auswahl.length > 0 ? (
        <Switch
          label="Nur ausgewählte anzeigen"
          checked={nurAusgewaehlte}
          onChange={(_e, data) => setNurAnsicht(data.checked ? new Set(gewaehlt) : undefined)}
        />
      ) : undefined}
      </div>

      {/* Der Export der Suche (ST-SEA-002 F2): sekundaer neben der Zahl,
          nur bei aktiver Suche mit Treffern -- ohne Suchgrund lehnt der Server
          ohnehin ab. */}
      {sucheAktiv && status === 'geladen' && parties.length > 0 ? (
        <div className={own.weiterwege}>
          <ExportKnopf
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            quelle={{ art: 'suche', suche: { term: aktiverBegriff, filter: aktiverFilter }, zahl: trefferWorte }}
            // Nur, was die Zeilen gerade zeigen (auswahlBereit); sonst das ganze Ergebnis.
            markiert={auswahlBereit ? auswahl.map((party) => party.partyId) : []}
            offen={exportDialogOffen}
            onOffen={setExportDialogOffen}
            // Den Fokus nicht anfassen: der Dialog ist dann noch offen, oder
            // "Abbrechen" hat ihn schon an "Exportieren" zurueckgegeben.
            onUnklar={() => setExportMeldung((alt) => ({ art: 'unklar', nr: (alt?.nr ?? 0) + 1 }))}
            onAngelegt={(_item, weggelassen) => {
              fokusAufExport.current = true;
              setExportMeldung((alt) => ({ art: 'angelegt', weggelassen, nr: (alt?.nr ?? 0) + 1 }));
            }}
          />
          {/* Sekundaer wie der Export und mit derselben Bedingung (Bauvorgabe 3a,
              Abschnitt 4); die Hauptaktion der Seite bleibt unveraendert. */}
          <ZurSammlungKnopf
            apiBaseUrl={apiBaseUrl}
            accessToken={accessToken}
            onReauthenticate={onReauthenticate}
            suche={{ term: aktiverBegriff, filter: aktiverFilter }}
            markiert={auswahlBereit ? auswahl.map((party) => party.partyId) : []}
            zahl={trefferWorte}
          />
        </div>
      ) : undefined}
      {exportMeldung === undefined ? undefined : <ExportMeldung key={exportMeldung.nr} art={exportMeldung.art} weggelassen={exportMeldung.weggelassen} onSchliessen={() => setExportMeldung(undefined)} />}

      {/* "Meine Massenlaeufe" steht nicht mehr hier: der Weg ist der
          Navigationseintrag "Massenpflege", und wer ihn nicht hat, dem fuehrte
          der Link auf ein 403 (keine Wege ins Leere). */}

      {/* Ohne Zeilen steht die Zahl trotzdem da, etwa wenn die
          wiedergefundene Suche nichts mehr liefert: "Stichwort pflegen" darf
          nie fuer Kontakte angeboten werden, von denen der Bildschirm nichts
          sagt. */}
      {parties.length === 0 && auswahl.length > 0 ? (
        <Caption1 className={own.hint}>
          {status === 'abgelehnt' ? selectionHeldNotice(auswahl.length) : selectionSummary(auswahl.length)}
        </Caption1>
      ) : undefined}


      {/* Ohne Treffer steht der Zustand in der Tabellenkarte, wo sonst die Tabelle steht (DESIGN F4). */}
      {status === 'geladen' && leer !== undefined ? (
        <Zustand art="leer" eingebettet ebene={2} titel={leer.titel} saetze={leer.saetze} {...(leer.haupt === undefined ? {} : { haupt: leer.haupt })} {...(leer.neben === undefined ? {} : { neben: leer.neben })} />
      ) : undefined}

      {parties.length > 0 ? (
        <Tabelle
          name={tabellenNameNachArt(tabellenArt)}
          zeilen={nurAnsicht === undefined ? parties : parties.filter((party) => nurAnsicht.has(party.partyId))}
          schluessel={zeilenSchluessel}
          spalten={spalten}
          // Slice 6c: eine Organisation klappt ihre Ansprechpartner:innen auf;
          // nach dem Laden steht die Anzahl am Knopf (M-09a). Eine Person mit
          // "und weitere" klappt ihre Organisationen auf (Restliste C5): die
          // Zelle kennt nur die erste, und der Name bleibt der einzige Link.
          aufklappen={{
            kann: (party) => party.kind === 'organization' || party.worksForMore === true,
            knopf: aufklappWort,
            // Der Name beginnt mit dem, was man sieht, samt Zahl (WCAG 2.5.3; axe 4.14
            // label-content-name-mismatch): "Ansprechpartner:innen 2 oder mehr, <Organisation>".
            // "2+" und den Mittelpunkt liest er nicht vor ("zwei plus").
            name: (party, anzahl) => {
              const knopf = aufklappWort(party);
              return anzahl === undefined
                ? `${knopf} von ${party.displayName}`
                : `${knopf} ${anzahl.endsWith('+') ? `${anzahl.slice(0, -1)} oder mehr` : anzahl}, ${party.displayName}`;
            },
            inhalt: (party, melden) =>
              party.kind !== 'organization' ? (
                <Verbundene
                  apiBaseUrl={apiBaseUrl}
                  accessToken={accessToken}
                  onReauthenticate={onReauthenticate}
                  partyId={party.partyId}
                  name={party.displayName}
                  onAnzahl={melden}
                  seite="person"
                />
              ) : (
              <Verbundene
                apiBaseUrl={apiBaseUrl}
                accessToken={accessToken}
                onReauthenticate={onReauthenticate}
                partyId={party.partyId}
                name={party.displayName}
                onAnzahl={melden}
                onAlsListe={() => {
                  // E3 S2b: die Liste zeigt genau die Ansprechpartner:innen, die der Knopf zaehlt. Der Aufklapper
                  // liest jeden beschreibbaren Zustand, also startet die Liste mit Inaktiven (E3 S2 Nachschnitt Bedeutung).
                  const filter: SearchFilter = {
                    ...EMPTY_SEARCH_FILTER,
                    includeInactive: true,
                    contactPersonOf: { partyId: party.partyId, displayName: party.displayName },
                  };
                  // Die Liste ersetzt die Suche ganz: ein Begriff im Feld oder ein alter
                  // Filterfehler galt nicht mehr, und der Begriff kaeme mit dem naechsten
                  // Enter unbemerkt zurueck.
                  angabenLauf.current += 1;
                  setEingabe('');
                  setFeldfehler(undefined);
                  setFilterFehler({});
                  setAngabenHinweis(undefined);
                  setUebernommen(undefined);
                  setEntwurf(filter);
                  sucheStarten(undefined, filter);
                  // Nach dem Laden die Ergebniszeile statt des Suchfelds (Restliste B7): Der Klick kam
                  // aus der Tabelle, und dort steht gleich das Ergebnis; das Feld ist leer.
                  fokusNachLaden.current = 'status';
                }}
              />
              ),
          }}
          auswahl={{
            gewaehlt: (party) => gewaehlt.has(party.partyId),
            umschalten: (party) => auswaehlen(toggleSelected(auswahl, { partyId: party.partyId, displayName: party.displayName })),
            name: (party) => `${party.displayName} auswählen`,
            // In der Ansicht "nur ausgewaehlte" gibt es keine Seite, die man ganz waehlen koennte.
            ...(nurAusgewaehlte
              ? {}
              : {
                  kopf: {
                    name: 'Alle auf dieser Seite auswählen',
                    stand: seiteGanz ? true : seite.some((party) => gewaehlt.has(party.partyId)) ? 'mixed' : false,
                    umschalten: () => auswaehlen(togglePage(auswahl, seite)),
                  },
                }),
          }}
          // „Weitere laden“ im Tabellenfuss (DESIGN F4, v4 .listfoot).
          fuss={
            cursor !== undefined && !nurAusgewaehlte ? (
              <Button id={MORE_BUTTON_ID} appearance="secondary" disabled={busy} onClick={weiterLaden}>
                Weitere laden
              </Button>
            ) : undefined
          }
        />
      ) : undefined}


      {auswahlBereit && !panelOffen ? (
        <div className={styles.actions}>
          {/* Ueber 250 bleibt der Knopf an seinem Platz und primary, damit nichts
              springt, aber disabledFocusable: per Tab erreichbar, mit seinem Grund. */}
          <Button
            appearance="primary"
            disabledFocusable={zuGrossFuerPflege}
            {...(zuGrossFuerPflege ? { 'aria-describedby': PFLEGE_GRENZE_ID } : {})}
            onClick={() => void panelOeffnen()}
          >
            Stichwort pflegen
          </Button>
          {/* Nur ohne Suche: mit Suche steht "Zur Sammlung …" schon neben dem Export und bliebe hier
              doppelt. Die Auswahl bleibt nach dem Hinzufuegen, damit gleich "Stichwort pflegen" folgen kann. */}
          {!sucheAktiv ? (
            <ZurSammlungKnopf
              apiBaseUrl={apiBaseUrl}
              accessToken={accessToken}
              onReauthenticate={onReauthenticate}
              suche={{}}
              markiert={auswahl.map((party) => party.partyId)}
              zahl=""
              ergebnisUnten
            />
          ) : undefined}
          {/* Ohne Suche exportiert die Auswahl (Michael E4 a); mit Suche steht
              "Exportieren" schon neben der Zahl und nimmt die Auswahl mit. */}
          {!sucheAktiv ? (
            <ExportKnopf
              apiBaseUrl={apiBaseUrl}
              accessToken={accessToken}
              onReauthenticate={onReauthenticate}
              quelle={AUSWAHL_QUELLE}
              markiert={auswahl.map((party) => party.partyId)}
              offen={exportDialogOffen}
              onOffen={setExportDialogOffen}
              onUnklar={() => setExportMeldung((alt) => ({ art: 'unklar', nr: (alt?.nr ?? 0) + 1 }))}
              onAngelegt={(_item, weggelassen) => {
                fokusAufExport.current = true;
                setExportMeldung((alt) => ({ art: 'angelegt', weggelassen, nr: (alt?.nr ?? 0) + 1 }));
              }}
            />
          ) : undefined}
          <Button appearance="secondary" onClick={() => auswaehlen([])}>
            Auswahl leeren
          </Button>
        </div>
      ) : undefined}
      {auswahlBereit && !panelOffen && zuGrossFuerPflege ? (
        <Body1 id={PFLEGE_GRENZE_ID} className={own.hint}>
          {maintenanceLimitSentence(auswahl.length)}
        </Body1>
      ) : undefined}

      {auswahlBereit && panelOffen ? (
        <section className={own.panel} aria-labelledby={PANEL_TITLE_ID}>
          <Body1 id={PANEL_TITLE_ID} tabIndex={-1}>
            <strong>Stichwort pflegen</strong> — {selectionSummary(auswahl.length)}
          </Body1>
          <div
            aria-live="polite"
            role="status"
            aria-label="Status: Stichwort pflegen"
            id={PANEL_STATUS_REGION_ID}
            tabIndex={-1}
          >
            {begriffeStatus === 'laedt' ? <Spinner labelPosition="after" label="Stichworte werden geladen" /> : undefined}
            {panelFehler !== undefined ? (
              <MessageBar intent={meldungsIntent(panelFehler, panelFehler === PREVIEW_UNCERTAIN_MESSAGE)}>
                <MessageBarBody>
                  <MessageBarTitle>{panelFehler.headline}</MessageBarTitle>
                  <div>{panelFehler.explanation}</div>
                  <div>{panelFehler.nextStep}</div>
                </MessageBarBody>
              </MessageBar>
            ) : undefined}
          </div>
          <form noValidate onSubmit={(event) => void vorschauZeigen(event)} className={own.panel}>
            <Field label="Was soll passieren?">
              <RadioGroup value={operation} onChange={(_event, data) => setOperation(data.value === 'remove' ? 'remove' : 'assign')}>
                <Radio value="assign" label="Stichwort vergeben" />
                <Radio value="remove" label="Stichwort entfernen" />
              </RadioGroup>
            </Field>
            {/* Ueber 20 vergebbaren Stichworten wird die Wahl ein Suchfeld (DESIGN 15.10.); ein Stichwort, das neue ersetzt das gewaehlte. */}
            {begriffeStatus === 'geladen' && brauchtSuchfeld(begriffe.length, begriffeGekuerzt) ? (
              <>
              <StichwortSuchfeld
                label="Stichwort"
                hinweis="Ein Stichwort je Lauf; ein neu gewähltes ersetzt das gewählte. Angeboten werden die Stichworte, die gerade vergeben werden können."
                eingabeId={PANEL_TERM_INPUT_ID}
                fehler={begriffFehler}
                einzeln
                gesperrt={panelBusy}
                ersteSeite={begriffe.map(alsEintrag)}
                ersteSeiteGekuerzt={begriffeGekuerzt}
                suchen={stichwortSuchen}
                gewaehlt={begriffEintrag === undefined ? [] : [begriffEintrag]}
                onHinzufuegen={(eintrag) => {
                  setBegriff(eintrag.id);
                  setBegriffGesucht(eintrag);
                  setBegriffFehler(undefined);
                  return true;
                }}
                onEntfernen={() => {
                  setBegriff(undefined);
                  setBegriffGesucht(undefined);
                }}
              />
              {/* Die Definition trennt gleichnamige Stichworte; das Suchfeld zeigt nur Name und Liste. */}
              {begriffEintrag?.beschreibung ? (
                <Caption1>
                  „{begriffEintrag.name}“: {begriffEintrag.beschreibung}
                </Caption1>
              ) : undefined}
              </>
            ) : (
            <Field
              label="Stichwort"
              hint="Die Auswahl zeigt den Begriff und wofür er steht. Angeboten werden die Stichworte, die gerade vergeben werden können."
              validationMessage={begriffFehler ?? null}
              validationState={begriffFehler === undefined ? 'none' : 'error'}
            >
              {begriffeGekuerzt ? <Caption1>{truncationNotice(begriffe.length)}</Caption1> : undefined}
              <Dropdown
                id={PANEL_TERM_INPUT_ID}
                disabled={panelBusy || begriffeStatus !== 'geladen'}
                placeholder="Stichwort wählen"
                value={begriffe.find((eintrag) => eintrag.taxonomyTermId === begriff)?.displayName ?? ''}
                selectedOptions={begriff === undefined ? [] : [begriff]}
                onOptionSelect={(_event, data) => {
                  setBegriff(data.optionValue);
                  setBegriffFehler(undefined);
                }}
              >
                {begriffe.map((eintrag) => (
                  <Option key={eintrag.taxonomyTermId} value={eintrag.taxonomyTermId} text={eintrag.displayName}>
                    <div>
                      <Body1>{eintrag.displayName}</Body1>
                      <Caption1>{eintrag.definition}</Caption1>
                    </div>
                  </Option>
                ))}
              </Dropdown>
            </Field>
            )}
            <Caption1>
              Die Vorschau zeigt, was sich ändert, bevor etwas passiert. Ab 100 Änderungen gibt eine zweite Person frei.
            </Caption1>
            {/* Waechst die Auswahl bei offenem Feld ueber 250, sagt das Feld denselben
                Grund -- sonst gaebe es einen 422 im Normalweg. */}
            {/* Immer eingehaengt und polite: waechst die Auswahl bei offenem Feld ueber 250, wird der Grund angesagt. */}
            <div aria-live="polite">
              {zuGrossFuerPflege ? <Body1 id={PFLEGE_GRENZE_FELD_ID}>{maintenanceLimitSentence(auswahl.length)}</Body1> : undefined}
            </div>
            <div className={styles.actions}>
              <Button
                type="submit"
                appearance="primary"
                // Ueber 250 nur disabledFocusable: `disabled` wuerde den Knopf und damit seinen Grund unerreichbar machen.
                disabled={!zuGrossFuerPflege && (panelBusy || begriffeStatus !== 'geladen')}
                disabledFocusable={zuGrossFuerPflege}
                {...(zuGrossFuerPflege ? { 'aria-describedby': PFLEGE_GRENZE_FELD_ID } : {})}
              >
                Vorschau zeigen
              </Button>
              <NebenKnopf disabled={panelBusy} onClick={() => setPanelOffen(false)}>
                Abbrechen
              </NebenKnopf>
            </div>
          </form>
        </section>
      ) : undefined}

      {status === 'abgelehnt' && message?.retryable === true ? (
        <div className={styles.actions}>
          <Button
            appearance="secondary"
            disabled={busy}
            onClick={() => void load(aktiverBegriff, aktiverFilter, fehlgeschlagenerCursor, listenArt)}
          >
            Erneut versuchen
          </Button>
        </div>
      ) : undefined}
    </Card>
  );
}

/**
 * Der Satz fuer die Statusregion nach dem Abschicken mit Angaben (DESIGN
 * vorgabe-pql-s1, Abschnitt 4): EINE Ansage, denn der eigene Text verschwindet
 * aus dem Feld, und das muss jemand sagen.
 */
function uebernommenSatz(aufgeloest: ResolvedS1, hausName: string | undefined): string | undefined {
  const teile = [
    ...Object.values(aufgeloest.termNames).map((begriff) => `Stichwort ${begriff.name}`),
    ...(hausName === undefined ? [] : [`für ${hausName}`]),
    ...aufgeloest.roleCodes.map((code) => `Rolle ${roleLabel(code)}`),
    ...(aufgeloest.postalCodePrefix === undefined ? [] : [`PLZ ${aufgeloest.postalCodePrefix}`]),
    ...(aufgeloest.locality === undefined ? [] : [`Ort ${aufgeloest.locality}`]),
    ...(aufgeloest.excludeInactive ? ['ohne Inaktive'] : []),
  ];
  return teile.length === 0 ? undefined : `In die Filter übernommen: ${teile.join(', ')}.`;
}

/**
 * Die angewandten Filter als Suchtext fuer „Als Suchtext kopieren“. Fehlt ein
 * Name (Auswahl noch nicht geladen), gibt es keinen Text und keinen Knopf,
 * statt einen falschen Text zu kopieren. Stichwoerter tragen ihre Achse,
 * damit der Text eindeutig bleibt.
 */
export function aktiverSuchtext(
  begriff: string | undefined,
  filter: SearchFilter,
  begriffe: readonly { readonly taxonomyTermId: string; readonly displayName: string; readonly taxonomyDisplayName: string }[],
  haeuser: readonly { readonly operatingContextId: string; readonly displayName: string }[],
): string | undefined {
  const keywords = filter.taxonomyTermIds.map((id) => {
    const bekannt = filter.termNames?.[id];
    if (bekannt !== undefined) return { name: bekannt.name, axis: bekannt.axis };
    const begriff = begriffe.find((eintrag) => eintrag.taxonomyTermId === id);
    return begriff === undefined ? undefined : { name: begriff.displayName, axis: begriff.taxonomyDisplayName };
  });
  if (keywords.some((keyword) => keyword === undefined)) return undefined;
  const haus = filter.operatingContextId === undefined ? undefined : haeuser.find((eintrag) => eintrag.operatingContextId === filter.operatingContextId);
  if (filter.operatingContextId !== undefined && haus === undefined) return undefined;
  // Die Suchsprache kennt „Ansprechpartner:innen von“ (noch) nicht; eine Kopie ohne ihn suchte etwas anderes.
  if (filter.contactPersonOf !== undefined) return undefined;
  return s1FormToText({
    freeText: begriff,
    keywords: keywords as { readonly name: string; readonly axis: string }[],
    houseName: haus?.displayName,
    roleCodes: filter.roleCodes,
    postalCodePrefix: filter.postalCodePrefix.trim() === '' ? undefined : filter.postalCodePrefix.trim(),
    locality: filter.locality.trim() === '' ? undefined : filter.locality.trim(),
    excludeInactive: false,
  });
}
